import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ConversationFlowEditor from './ConversationFlowEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminGameEditor() {
  const [activeTab, setActiveTab] = useState('locations');
  const [locations, setLocations] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState(null);
  const [taxiSettings, setTaxiSettings] = useState({
    position: { x: 500, y: 400, z: 0 },
    size: 60,
    rotation: 0,
    flipH: false,
    flipV: false,
    style: 'classic',
    animationSpeed: 0.01,
    image_url: ''
  });
  const [userMaleAvatarSettings, setUserMaleAvatarSettings] = useState({
    position: { x: 300, y: 300, z: 0 },
    size: 80,
    rotation: 0,
    flipH: false,
    flipV: false,
    image_url: '',
    emoji: '👨'
  });
  const [userFemaleAvatarSettings, setUserFemaleAvatarSettings] = useState({
    position: { x: 400, y: 300, z: 0 },
    size: 80,
    rotation: 0,
    flipH: false,
    flipV: false,
    image_url: '',
    emoji: '👩'
  });
  const [markers, setMarkers] = useState([]);
  const [routes, setRoutes] = useState([]); // Route groups containing markers
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [hiddenGems, setHiddenGems] = useState([]);
  const [selectedGemId, setSelectedGemId] = useState(null);
  const [dayNightSettings, setDayNightSettings] = useState({
    dayDuration: 30,
    nightDuration: 10
  });
  const [gemFeatures, setGemFeatures] = useState({
    dailyLoginRewards: true,
    rankedMode: true,
    lowBalanceNotifications: true,
    spendingLock: false,
    smartSuggestions: true,
    savingsGoals: true
  });
  const [rankConfig, setRankConfig] = useState([
    { name: 'Beginner', minGems: 0, icon: '🌱' },
    { name: 'Intermediate', minGems: 500, icon: '⭐' },
    { name: 'Advanced', minGems: 1000, icon: '💫' },
    { name: 'Expert', minGems: 2000, icon: '🏆' },
    { name: 'Legend', minGems: 5000, icon: '👑' }
  ]);
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [gameSettings, setGameSettings] = useState({
    starting_gems: 1000,
    requires_membership: false,
    game_price: 0
  });
  const [stopBubbleSettings, setStopBubbleSettings] = useState({
    offsetX: -60,
    offsetY: -60,
    rotation: 0,
    size: 180
  });
  const [loading, setLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState(null);
  const [previewWindow, setPreviewWindow] = useState(null);
  const [pickupItems, setPickupItems] = useState([]);
  const [itemSpawns, setItemSpawns] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedSpawnId, setSelectedSpawnId] = useState(null);
  const [roads, setRoads] = useState([]); // Road Network System
  const [selectedRoadId, setSelectedRoadId] = useState(null);
  const [roadNodes, setRoadNodes] = useState([]); // Manual road node placement
  const [placingNodes, setPlacingNodes] = useState(false); // Node placement mode


  const loadGameData = async () => {
    console.log('🔄 Loading game data...');
    try {
      const response = await axios.get(`${API_URL}/api/game/config`);
      const config = response.data;
      console.log('✅ Game config loaded:', Object.keys(config));
      
      setLocations(config.locations || []);
      setCharacters(config.characters || []);
      // Filter out invalid/phantom markers during load
      setMarkers((config.markers || []).filter(marker => marker && marker.id && marker.position));
      setRoutes(config.routes || []); // Load route groups
      setHiddenGems(config.hidden_gems || []);
      setPickupItems(config.pickup_items || []);
      setItemSpawns(config.item_spawns || []);
      setRoads(config.roads || []); // Load road network
      setRoadNodes(config.road_nodes || []); // Load manual road nodes
      setBackgroundUrl(config.background_url || '');
      
      if (config.taxi_settings) {
        setTaxiSettings(config.taxi_settings);
      }
      
      if (config.user_male_avatar_settings) {
        setUserMaleAvatarSettings(config.user_male_avatar_settings);
      }
      
      if (config.user_female_avatar_settings) {
        setUserFemaleAvatarSettings(config.user_female_avatar_settings);
      }
      
      if (config.day_night_settings) {
        setDayNightSettings(config.day_night_settings);
      }

      if (config.gem_features) {
        setGemFeatures(config.gem_features);
      }
      
      if (config.stop_bubble_settings) {
        setStopBubbleSettings(config.stop_bubble_settings);
      }
      
      setGameSettings({
        starting_gems: config.starting_gems || 1000,
        requires_membership: config.requires_membership || false,
        game_price: config.game_price || 0
      });
      
      setLoading(false);
      console.log('✅ Game data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading game data:', error);
      setLoading(false);
    }
  };

  // Clear markers state on mount to prevent phantom markers, then load fresh data
  useEffect(() => {
    console.log('🔄 AdminGameEditor mounted - clearing stale state');
    setMarkers([]); // Ensure clean state
    setRoutes([]); // Clear routes too
    loadGameData();
  }, []);
  
  // Listen for road node placements and moves from preview window
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'ROAD_NODE_PLACED') {
        const newNode = {
          id: `node_${Date.now()}`,
          x: event.data.x,
          y: event.data.y
        };
        setRoadNodes(prev => [...prev, newNode]);
        console.log('📍 Received node:', newNode);
      } else if (event.data?.type === 'ROAD_NODE_MOVED') {
        // Update node position when dragged in preview
        setRoadNodes(prev => {
          const updated = [...prev];
          if (updated[event.data.index]) {
            updated[event.data.index] = {
              ...updated[event.data.index],
              x: event.data.x,
              y: event.data.y
            };
          }
          return updated;
        });
        console.log(`📍 Node #${event.data.index + 1} moved to (${event.data.x}, ${event.data.y})`);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Cleanup preview window on unmount
  useEffect(() => {
    return () => {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    };
  }, [previewWindow]);

  const saveGameData = async () => {
    try {
      // FIXED: GET latest config, MERGE changes, then POST
      // This prevents data loss when saving from different tabs
      
      // Step 1: GET latest full configuration from database
      const latestConfigRes = await axios.get(`${API_URL}/api/game/config`);
      const latestConfig = latestConfigRes.data;
      
      // Step 2: MERGE current editor state with latest config
      const mergedConfig = {
        ...latestConfig, // Start with what's in database
        // Now overlay with current editor state
        background_url: backgroundUrl,
        characters: characters,
        locations: locations,
        markers: markers,
        routes: routes,
        roads: roads, // Road Network System
        road_nodes: roadNodes, // Manual road node placement
        hidden_gems: hiddenGems,
        taxi_settings: taxiSettings,
        stop_bubble_settings: stopBubbleSettings,
        user_male_avatar_settings: userMaleAvatarSettings,
        user_female_avatar_settings: userFemaleAvatarSettings,
        day_night_settings: dayNightSettings,
        gem_features: gemFeatures,
        pickup_items: pickupItems,
        item_spawns: itemSpawns,
        taxi_cost: gameSettings.taxi_cost || 200,
        hotel_cost: gameSettings.hotel_cost || 500,
        starting_gems: gameSettings.starting_gems || 1000
      };
      
      // Debug logging
      console.log('💾 Saving config...');
      console.log('  - Characters count:', mergedConfig.characters.length);
      console.log('  - Locations count:', mergedConfig.locations.length);
      mergedConfig.characters.forEach(char => {
        console.log(`  - Character "${char.name}": has image = ${char.image_url ? 'YES (' + char.image_url.substring(0, 30) + '...)' : 'NO'}`);
      });
      mergedConfig.locations.forEach(loc => {
        console.log(`  - Location "${loc.name}": position (${loc.marker_x}, ${loc.marker_y})`);
      });
      
      // Step 3: POST the complete merged config back
      await axios.post(`${API_URL}/api/game/config`, mergedConfig);
      
      // Clear preview data after saving
      await axios.delete(`${API_URL}/api/game/preview`);
      
      setSaveMessage('✅ Saved successfully!');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('❌ Save failed: ' + (error.response?.data?.detail || error.message));
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  const openPreview = () => {
    // Close existing preview window if open
    if (previewWindow && !previewWindow.closed) {
      previewWindow.close();
    }

    // Open new preview window (protected route - requires admin auth)
    const width = 1200;
    const height = 800;
    const left = window.screenX + 50;
    const top = window.screenY + 50;
    
    const newWindow = window.open(
      '/game-preview',
      'GamePreview',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    setPreviewWindow(newWindow);

    // Send current editor state to preview window
    if (newWindow) {
      // Wait a moment for the window to load, then send data
      setTimeout(() => {
        broadcastToPreview();
      }, 1000);
    }
  };

  // Location Management
  const addLocation = () => {
    const newLocation = {
      id: `loc_${Date.now()}`,
      name: 'New Location',
      marker_x: 800,  // Default to visible area (not 0,0)
      marker_y: 450,  // Default to visible area (not 0,0)
      marker_z: 0,
      size: 80,
      rotation: 0,
      flipH: false,
      flipV: false,
      description: '',
      bubble_text: ''
    };
    setLocations([...locations, newLocation]);
    setSelectedLocationId(newLocation.id);
  };

  const updateLocation = (id, field, value) => {
    const updatedLocations = locations.map(loc => 
      loc.id === id ? { ...loc, [field]: value } : loc
    );
    setLocations(updatedLocations);
  };

  const deleteLocation = (id) => {
    setLocations(locations.filter(loc => loc.id !== id));
    if (selectedLocationId === id) {
      setSelectedLocationId(null);
    }
  };

  // Character Management
  const addCharacter = () => {
    const newChar = {
      id: `char_${Date.now()}`,
      name: 'New Character',
      emoji: '👤',
      position: { x: 100, y: 100, z: 0 },
      size: 80,
      rotation: 0,
      flipH: false,
      flipV: false,
      image_url: '',
      conversations: [],
      is_translator: false,
      role: 'character'
    };
    setCharacters([...characters, newChar]);
    setSelectedCharacterId(newChar.id);
  };

  const updateCharacter = (id, field, value) => {
    const updatedCharacters = characters.map(char => 
      char.id === id ? { ...char, [field]: value } : char
    );
    setCharacters(updatedCharacters);
  };

  const deleteCharacter = (id) => {
    const updatedCharacters = characters.filter(char => char.id !== id);
    setCharacters(updatedCharacters);
    
    if (selectedCharacterId === id) {
      setSelectedCharacterId(null);
    }
  };

  // Taxi update function (for TaxiMarkersTab)
  const updateTaxiWithoutPreview = (updates) => {
    const newSettings = {...taxiSettings, ...updates};
    console.log('🚕 Taxi settings updated:', newSettings.position);
    setTaxiSettings(newSettings);
    // Broadcasting handled by useEffect when taxiSettings changes
  };

  // Broadcast current state to preview window
  const broadcastToPreview = useCallback(() => {
    // Only broadcast if preview window is open
    if (previewWindow && !previewWindow.closed) {
      try {
        console.log('📤 Broadcasting to preview:', {
          characters: characters.length,
          locations: locations.length,
          taxiPos: taxiSettings.position,
          placingNodes: placingNodes
        });
        
        // Create fresh channel each time to avoid "not usable" errors
        const channel = new BroadcastChannel('game-editor-preview');
        channel.postMessage({
          type: 'UPDATE_PREVIEW',
          data: {
            locations,
            characters,
            // Filter out invalid/phantom markers before broadcasting
            markers: markers.filter(marker => marker && marker.id && marker.position),
            routes,
            hidden_gems: hiddenGems,
            background_url: backgroundUrl,
            taxi_settings: taxiSettings,  // snake_case for backend compatibility
            user_male_avatar_settings: userMaleAvatarSettings,
            user_female_avatar_settings: userFemaleAvatarSettings,
            day_night_settings: dayNightSettings,
            gem_features: gemFeatures,
            gameSettings,
            placingNodes: placingNodes,  // Send placing mode to preview
            road_nodes: roadNodes  // Send current nodes to preview
          }
        });
        channel.close(); // Close immediately after sending
        
        console.log('✅ Broadcast sent successfully');
      } catch (error) {
        console.warn('❌ Preview broadcast failed:', error.message);
      }
    } else {
      console.log('⚠️ Preview window not open, skipping broadcast');
    }
  }, [previewWindow, locations, characters, markers, routes, hiddenGems, backgroundUrl, taxiSettings, userMaleAvatarSettings, userFemaleAvatarSettings, dayNightSettings, gemFeatures, gameSettings, placingNodes, roadNodes]);

  // Broadcast to preview when state changes
  useEffect(() => {
    // Only broadcast if preview window is open
    if (previewWindow && !previewWindow.closed) {
      console.log('🔄 State changed, scheduling broadcast...');
      const timeoutId = setTimeout(() => {
        broadcastToPreview();
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      console.log('⏸️ Preview not open, skipping broadcast');
    }
  }, [locations, characters, markers, routes, taxiSettings, userMaleAvatarSettings, userFemaleAvatarSettings, backgroundUrl, previewWindow, broadcastToPreview, placingNodes, roadNodes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    };
  }, [previewWindow]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '6px solid #e3f2fd',
          borderTop: '6px solid #2196F3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{fontSize: '20px', color: '#666', fontWeight: '500'}}>
          Loading Game Editor...
        </div>
        <div style={{fontSize: '14px', color: '#999'}}>
          Please wait while we fetch your game configuration
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🎮 Morocco Journey - Game Editor</h1>
        <div style={{display: 'flex', gap: '10px'}}>
          <button onClick={openPreview} style={styles.previewBtn}>
            👁️ Preview Game
          </button>
          <button onClick={saveGameData} style={styles.saveBtn}>
            💾 Save All Changes
          </button>
        </div>
      </div>

      {saveMessage && (
        <div style={styles.saveMessage}>{saveMessage}</div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('locations')}
          style={{
            ...styles.tab,
            ...(activeTab === 'locations' ? styles.activeTab : {})
          }}
        >
          📍 Locations
        </button>
        <button
          onClick={() => setActiveTab('characters')}
          style={{
            ...styles.tab,
            ...(activeTab === 'characters' ? styles.activeTab : {})
          }}
        >
          👥 Characters
        </button>
        <button
          onClick={() => setActiveTab('taxi')}
          style={{
            ...styles.tab,
            ...(activeTab === 'taxi' ? styles.activeTab : {})
          }}
        >
          🚕 Taxi & Markers
        </button>
        <button
          onClick={() => setActiveTab('stop-bubble')}
          style={{
            ...styles.tab,
            ...(activeTab === 'stop-bubble' ? styles.activeTab : {})
          }}
        >
          🚦 Stop Bubble
        </button>
        <button
          onClick={() => setActiveTab('player-avatar-mjrny')}
          style={{
            ...styles.tab,
            ...(activeTab === 'player-avatar-mjrny' ? styles.activeTab : {})
          }}
        >
          👤 Player Avatar (You)
        </button>
        <button
          onClick={() => setActiveTab('gems')}
          style={{
            ...styles.tab,
            ...(activeTab === 'gems' ? styles.activeTab : {})
          }}
        >
          💎 Gems
        </button>
        <button
          onClick={() => setActiveTab('players')}
          style={{
            ...styles.tab,
            ...(activeTab === 'players' ? styles.activeTab : {})
          }}
        >
          👥 Player Management
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          style={{
            ...styles.tab,
            ...(activeTab === 'conversations' ? styles.activeTab : {})
          }}
        >
          💬 Conversations
        </button>
        <button
          onClick={() => setActiveTab('pickup-items')}
          style={{
            ...styles.tab,
            ...(activeTab === 'pickup-items' ? styles.activeTab : {})
          }}
        >
          📦 Pickup Items
        </button>
        <button
          onClick={() => setActiveTab('settings-mjrny')}
          style={{
            ...styles.tab,
            ...(activeTab === 'settings-mjrny' ? styles.activeTab : {})
          }}
        >
          ⚙️ Settings M-Journey
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.content}>
        {activeTab === 'locations' && (
          <LocationsTab
            locations={locations}
            routes={routes}
            markers={markers}
            characters={characters}
            selectedLocationId={selectedLocationId}
            setSelectedLocationId={setSelectedLocationId}
            addLocation={addLocation}
            updateLocation={updateLocation}
            deleteLocation={deleteLocation}
            saveGameData={saveGameData}
          />
        )}

        {activeTab === 'characters' && (
          <CharactersTab
            characters={characters}
            selectedCharacterId={selectedCharacterId}
            setSelectedCharacterId={setSelectedCharacterId}
            addCharacter={addCharacter}
            updateCharacter={updateCharacter}
            deleteCharacter={deleteCharacter}
            saveGameData={saveGameData}
          />
        )}

        {activeTab === 'taxi' && (
          <TaxiMarkersTab
            taxiSettings={taxiSettings}
            updateTaxiSettings={setTaxiSettings}
            updateTaxiWithoutPreview={updateTaxiWithoutPreview}
            markers={markers}
            setMarkers={setMarkers}
            routes={routes}
            setRoutes={setRoutes}
            selectedRouteId={selectedRouteId}
            setSelectedRouteId={setSelectedRouteId}
            roads={roads}
            setRoads={setRoads}
            selectedRoadId={selectedRoadId}
            setSelectedRoadId={setSelectedRoadId}
            roadNodes={roadNodes}
            setRoadNodes={setRoadNodes}
            placingNodes={placingNodes}
            setPlacingNodes={setPlacingNodes}
            characters={characters}
            locations={locations}
            saveGameData={saveGameData}
            previewWindow={previewWindow}
            broadcastToPreview={broadcastToPreview}
          />
        )}

        {activeTab === 'stop-bubble' && (
          <StopBubbleTab
            stopBubbleSettings={stopBubbleSettings}
            setStopBubbleSettings={setStopBubbleSettings}
            saveGameData={saveGameData}
          />
        )}

        {activeTab === 'player-avatar-mjrny' && (
          <PlayerAvatarTab
            userMaleAvatarSettings={userMaleAvatarSettings}
            setUserMaleAvatarSettings={setUserMaleAvatarSettings}
            userFemaleAvatarSettings={userFemaleAvatarSettings}
            setUserFemaleAvatarSettings={setUserFemaleAvatarSettings}
            saveGameData={saveGameData}
          />
        )}

        {activeTab === 'players' && (
          <PlayerManagementTab />
        )}

        {activeTab === 'gems' && (
          <GemsTab
            hiddenGems={hiddenGems}
            setHiddenGems={setHiddenGems}
            selectedGemId={selectedGemId}
            setSelectedGemId={setSelectedGemId}
            dayNightSettings={dayNightSettings}
            setDayNightSettings={setDayNightSettings}
            gemFeatures={gemFeatures}
            setGemFeatures={setGemFeatures}
            gameSettings={gameSettings}
            updateSettings={(field, value) => 
              setGameSettings({ ...gameSettings, [field]: value })
            }
            saveGameData={saveGameData}
          />
        )}

        {activeTab === 'conversations' && (
          <ConversationFlowEditor
            characters={characters}
            locations={locations}
            onSave={(flow) => console.log('Flow saved:', flow)}
          />
        )}


        {activeTab === 'pickup-items' && (
          <div>
            <h2>📦 Pickup Items Management</h2>
            <p style={{color: '#666', marginBottom: '20px'}}>
              Manage items that players can find and collect on the map
            </p>

            <button
              onClick={() => {
                const newItem = {
                  id: `item_${Date.now()}`,
                  name: 'New Item',
                  category: 'food',
                  emoji: '🍎',
                  rarity: 'common',
                  base_value: 10,
                  can_eat: false,
                  energy_restore: 0,
                  description: '',
                  stack_size: 10
                };
                setPickupItems([...pickupItems, newItem]);
                setSelectedItemId(newItem.id);
              }}
              style={styles.addBtn}
            >
              + Add Item
            </button>

            <div style={{display: 'flex', gap: '20px', marginTop: '20px'}}>
              {/* Items List */}
              <div style={{flex: 1, maxWidth: '300px'}}>
                <h3>Items ({pickupItems.length})</h3>
                {pickupItems.map(item => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItemId(item.id)}
                    style={{
                      ...styles.listItem,
                      backgroundColor: selectedItemId === item.id ? '#e3f2fd' : 'white'
                    }}
                  >
                    <span style={{fontSize: '24px', marginRight: '10px'}}>{item.emoji}</span>
                    <div>
                      <div style={{fontWeight: 'bold'}}>{item.name}</div>
                      <div style={{fontSize: '12px', color: '#666'}}>
                        {item.category} - {item.base_value} 💎
                      </div>
                    </div>
                  </div>
                ))}
                {pickupItems.length === 0 && (
                  <p style={{color: '#999', fontStyle: 'italic'}}>No items yet. Click + Add Item</p>
                )}
              </div>

              {/* Item Editor */}
              {selectedItemId && pickupItems.find(i => i.id === selectedItemId) && (
                <div style={{flex: 2}}>
                  {(() => {
                    const item = pickupItems.find(i => i.id === selectedItemId);
                    return (
                      <div style={styles.editorPanel}>
                        <h3>Edit Item: {item.name}</h3>

                        <label style={styles.label}>
                          Name:
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, name: e.target.value} : i
                              ));
                            }}
                            style={styles.input}
                          />
                        </label>

                        <label style={styles.label}>
                          Emoji Icon:
                          <input
                            type="text"
                            value={item.emoji}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, emoji: e.target.value} : i
                              ));
                            }}
                            style={styles.input}
                            placeholder="🍎"
                          />
                          <small style={{color: '#666'}}>Use emoji (will be replaced with PNG later)</small>
                        </label>

                        <label style={styles.label}>
                          Category:
                          <select
                            value={item.category}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, category: e.target.value} : i
                              ));
                            }}
                            style={styles.input}
                          >
                            <option value="food">🍎 Food</option>
                            <option value="nature">🌿 Nature</option>
                          </select>
                        </label>

                        <label style={styles.label}>
                          Rarity:
                          <select
                            value={item.rarity}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, rarity: e.target.value} : i
                              ));
                            }}
                            style={styles.input}
                          >
                            <option value="common">Common</option>
                            <option value="uncommon">Uncommon</option>
                            <option value="rare">Rare</option>
                          </select>
                        </label>

                        <label style={styles.label}>
                          Base Value (gems):
                          <input
                            type="number"
                            value={item.base_value}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, base_value: parseInt(e.target.value)} : i
                              ));
                            }}
                            style={styles.input}
                          />
                        </label>

                        <label style={styles.label}>
                          <input
                            type="checkbox"
                            checked={item.can_eat}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, can_eat: e.target.checked} : i
                              ));
                            }}
                          />
                          Can be eaten (food items)
                        </label>

                        {item.can_eat && (
                          <label style={styles.label}>
                            Energy Restore (%):
                            <input
                              type="number"
                              value={item.energy_restore}
                              onChange={(e) => {
                                setPickupItems(pickupItems.map(i => 
                                  i.id === item.id ? {...i, energy_restore: parseInt(e.target.value)} : i
                                ));
                              }}
                              style={styles.input}
                            />
                          </label>
                        )}

                        <label style={styles.label}>
                          Stack Size:
                          <input
                            type="number"
                            value={item.stack_size}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, stack_size: parseInt(e.target.value)} : i
                              ));
                            }}
                            style={styles.input}
                          />
                          <small style={{color: '#666'}}>Max items per stack</small>
                        </label>

                        <label style={styles.label}>
                          Description:
                          <textarea
                            value={item.description}
                            onChange={(e) => {
                              setPickupItems(pickupItems.map(i => 
                                i.id === item.id ? {...i, description: e.target.value} : i
                              ));
                            }}
                            style={{...styles.input, minHeight: '80px'}}
                          />
                        </label>

                        <div style={{marginTop: '20px'}}>
                          <h4>Spawn Points for this Item</h4>
                          <button
                            onClick={() => {
                              const newSpawn = {
                                id: `spawn_${Date.now()}`,
                                item_id: item.id,
                                spawn_type: 'fixed',
                                position: {x: 500, y: 400},
                                respawn_minutes: 30
                              };
                              setItemSpawns([...itemSpawns, newSpawn]);
                            }}
                            style={{...styles.addBtn, marginBottom: '10px'}}
                          >
                            + Add Spawn Point
                          </button>

                          {itemSpawns.filter(s => s.item_id === item.id).map(spawn => (
                            <div key={spawn.id} style={{backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '5px', marginBottom: '10px'}}>
                              <div style={{display: 'flex', gap: '10px'}}>
                                <label style={{...styles.label, flex: 1}}>
                                  X:
                                  <input
                                    type="number"
                                    value={spawn.position?.x || 0}
                                    onChange={(e) => {
                                      setItemSpawns(itemSpawns.map(s => 
                                        s.id === spawn.id ? {...s, position: {...s.position, x: parseInt(e.target.value)}} : s
                                      ));
                                    }}
                                    style={styles.input}
                                  />
                                </label>
                                <label style={{...styles.label, flex: 1}}>
                                  Y:
                                  <input
                                    type="number"
                                    value={spawn.position?.y || 0}
                                    onChange={(e) => {
                                      setItemSpawns(itemSpawns.map(s => 
                                        s.id === spawn.id ? {...s, position: {...s.position, y: parseInt(e.target.value)}} : s
                                      ));
                                    }}
                                    style={styles.input}
                                  />
                                </label>
                                <button
                                  onClick={() => {
                                    setItemSpawns(itemSpawns.filter(s => s.id !== spawn.id));
                                  }}
                                  style={{...styles.deleteButton, alignSelf: 'flex-end'}}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={() => {
                            setPickupItems(pickupItems.filter(i => i.id !== item.id));
                            setItemSpawns(itemSpawns.filter(s => s.item_id !== item.id));
                            setSelectedItemId(null);
                          }}
                          style={{...styles.deleteButton, marginTop: '20px'}}
                        >
                          🗑️ Delete Item
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <button onClick={saveGameData} style={{...styles.saveButton, marginTop: '30px'}}>
              💾 Save All Items & Spawns
            </button>
          </div>
        )}


        {activeTab === 'settings-mjrny' && (
          <SettingsMJourneyTab
            backgroundUrl={backgroundUrl}
            setBackgroundUrl={setBackgroundUrl}
            gameSettings={gameSettings}
            updateSettings={(field, value) => 
              setGameSettings({ ...gameSettings, [field]: value })
            }
            saveGameData={saveGameData}
          />
        )}

      </div>
    </div>
  );
}
// Locations Tab Component
function LocationsTab({ locations, routes, markers, characters, selectedLocationId, setSelectedLocationId, addLocation, updateLocation, deleteLocation, saveGameData }) {
  const selectedLocation = locations.find(loc => loc.id === selectedLocationId);

  return (
    <div style={styles.tabContent}>
      <div style={styles.tabHeader}>
        <h2>📍 Location Management</h2>
        <button onClick={addLocation} style={styles.addBtn}>
          + Add Location
        </button>
      </div>

      <div style={styles.splitLayout}>
        {/* Location List */}
        <div style={styles.listPanel}>
          <h3 style={styles.panelTitle}>Locations</h3>
          {locations.map(location => (
            <div
              key={location.id}
              onClick={() => setSelectedLocationId(location.id)}
              style={{
                ...styles.listItem,
                ...(selectedLocationId === location.id ? styles.selectedItem : {})
              }}
            >
              <span style={{fontSize: '20px', marginRight: '10px'}}>📍</span>
              <span style={{fontWeight: 'bold'}}>{location.name}</span>
              {selectedLocationId === location.id && (
                <span style={styles.highlightBadge}>Selected</span>
              )}
            </div>
          ))}
          {locations.length === 0 && (
            <div style={styles.emptyState}>No locations yet</div>
          )}
        </div>

        {/* Location Editor */}
        <div style={styles.editorPanel}>
          {selectedLocation ? (
            <>
              <h3 style={styles.panelTitle}>Edit Location: {selectedLocation.name}</h3>
              
              <label style={styles.label}>
                Location Name:
                <input
                  type="text"
                  value={selectedLocation.name}
                  onChange={(e) => updateLocation(selectedLocation.id, 'name', e.target.value)}
                  style={styles.input}
                  placeholder="e.g., Airport, Restaurant, Hotel"
                />
              </label>

              <label style={styles.label}>
                Bubble Text (shown on map):
                <input
                  type="text"
                  value={selectedLocation.bubble_text || ''}
                  onChange={(e) => updateLocation(selectedLocation.id, 'bubble_text', e.target.value)}
                  style={styles.input}
                  placeholder="e.g., AIRPORT"
                />
              </label>

              <label style={styles.label}>
                Description (info for users):
                <textarea
                  value={selectedLocation.description || ''}
                  onChange={(e) => updateLocation(selectedLocation.id, 'description', e.target.value)}
                  style={styles.textarea}
                  placeholder="e.g., This is the airport where your journey begins..."
                />
              </label>

              <div style={styles.controlsSection}>
                <h4>Position & Appearance</h4>
                
                <label style={styles.label}>
                  X-Axis (Left ← → Right):
                  <input
                    type="range"
                    min="0"
                    max="3000"
                    value={selectedLocation.marker_x}
                    onChange={(e) => updateLocation(selectedLocation.id, 'marker_x', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedLocation.marker_x}px</span>
                </label>

                <label style={styles.label}>
                  Y-Axis (Top ↑ ↓ Down):
                  <input
                    type="range"
                    min="0"
                    max="2000"
                    value={selectedLocation.marker_y}
                    onChange={(e) => updateLocation(selectedLocation.id, 'marker_y', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedLocation.marker_y}px</span>
                </label>

                <label style={styles.label}>
                  Z-Axis (Back ↗ ↙ Front):
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={selectedLocation.marker_z || 0}
                    onChange={(e) => updateLocation(selectedLocation.id, 'marker_z', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedLocation.marker_z || 0}px</span>
                </label>

                <label style={styles.label}>
                  Size:
                  <input
                    type="range"
                    min="40"
                    max="150"
                    value={selectedLocation.size || 80}
                    onChange={(e) => updateLocation(selectedLocation.id, 'size', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedLocation.size || 80}px</span>
                </label>

                <label style={styles.label}>
                  Manual Rotation:
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={selectedLocation.rotation || 0}
                    onChange={(e) => updateLocation(selectedLocation.id, 'rotation', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedLocation.rotation || 0}°</span>
                </label>

                <div style={styles.checkboxGroup}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={selectedLocation.flipH || false}
                      onChange={(e) => updateLocation(selectedLocation.id, 'flipH', e.target.checked)}
                    />
                    Flip Horizontal
                  </label>

                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={selectedLocation.flipV || false}
                      onChange={(e) => updateLocation(selectedLocation.id, 'flipV', e.target.checked)}
                    />
                    Flip Vertical (Mirror)
                  </label>
                </div>
              </div>

              {/* HOTEL/RESTAURANT TYPE MANAGEMENT */}
              {selectedLocation && (
              <div style={{...styles.controlsSection, marginTop: '30px', borderTop: '2px solid #ddd', paddingTop: '20px'}}>
                <h4>🏨 Location Type & Services</h4>
                
                <label style={styles.label}>
                  Location Type:
                  <select
                    value={selectedLocation.location_type || 'landmark'}
                    onChange={(e) => {
                      const type = e.target.value;
                      updateLocation(selectedLocation.id, 'location_type', type);
                      
                      // Initialize data structures if switching to hotel/restaurant/gym
                      if (type === 'hotel' && !selectedLocation.hotel_data) {
                        updateLocation(selectedLocation.id, 'hotel_data', {
                          rooms: [],
                          amenities: [],
                          receptionist_character_id: null,
                          services: {
                            room_service: true,
                            laundry: true,
                            concierge: true,
                            spa: false,
                            taxi_booking: true
                          },
                          dynamic_pricing: {
                            enabled: true,
                            base_multiplier: 1.0,
                            peak_hours: [18, 19, 20, 21],
                            peak_multiplier: 1.3,
                            weekend_multiplier: 1.2
                          }
                        });
                      } else if (type === 'restaurant' && !selectedLocation.restaurant_data) {
                        updateLocation(selectedLocation.id, 'restaurant_data', {
                          menu: [],
                          waiter_character_id: null,
                          dynamic_pricing: {
                            enabled: true,
                            base_multiplier: 1.0,
                            lunch_hours: [12, 13, 14],
                            lunch_discount: 0.8,
                            dinner_hours: [19, 20, 21],
                            dinner_premium: 1.2
                          }
                        });
                      } else if (type === 'gym' && !selectedLocation.gym_data) {
                        updateLocation(selectedLocation.id, 'gym_data', {
                          membership_price: 200,
                          daily_pass_price: 50,
                          energy_cost: 30,
                          trainer_character_id: null
                        });
                      } else if (type === 'shop' && !selectedLocation.shop_data) {
                        updateLocation(selectedLocation.id, 'shop_data', {
                          items: [],
                          shopkeeper_character_id: null
                        });
                      }
                    }}
                    style={styles.input}
                  >
                    <option value="landmark">🗺️ Landmark (General)</option>
                    <option value="hotel">🏨 Hotel</option>
                    <option value="restaurant">🍽️ Restaurant</option>
                    <option value="gym">💪 Gym</option>
                    <option value="shop">🛒 Shop</option>
                    <option value="cafe">☕ Cafe</option>
                    <option value="museum">🏛️ Museum</option>
                    <option value="park">🌳 Park</option>
                    <option value="market">🏪 Market</option>
                  </select>
                </label>

                {/* HOTEL CONFIGURATION */}
                {selectedLocation.location_type === 'hotel' && (
                  <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#F3E5F5', borderRadius: '8px'}}>
                    <h4>🏨 Hotel Configuration</h4>
                    
                    <label style={styles.label}>
                      Receptionist Character:
                      <select
                        value={selectedLocation.hotel_data?.receptionist_character_id || ''}
                        onChange={(e) => {
                          const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: [], services: {}, dynamic_pricing: {}};
                          updateLocation(selectedLocation.id, 'hotel_data', {
                            ...hotelData,
                            receptionist_character_id: e.target.value || null
                          });
                        }}
                        style={styles.input}
                      >
                        <option value="">-- No Character --</option>
                        {characters.map(char => (
                          <option key={char.id} value={char.id}>{char.emoji} {char.name}</option>
                        ))}
                      </select>
                      <small style={{color: '#666', fontSize: '12px'}}>
                        💡 Select receptionist for "Speak to Receptionist" feature
                      </small>
                    </label>

                    <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#E3F2FD', borderRadius: '5px'}}>
                      <h5 style={{margin: '0 0 10px 0'}}>🛎️ Hotel Services (What guests can ask for):</h5>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                        {['room_service', 'laundry', 'concierge', 'spa', 'taxi_booking', 'wake_up_call', 'minibar', 'iron'].map(service => (
                          <label key={service} style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px'}}>
                            <input
                              type="checkbox"
                              checked={selectedLocation.hotel_data?.services?.[service] || false}
                              onChange={(e) => {
                                const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: [], services: {}, dynamic_pricing: {}};
                                updateLocation(selectedLocation.id, 'hotel_data', {
                                  ...hotelData,
                                  services: {
                                    ...hotelData.services,
                                    [service]: e.target.checked
                                  }
                                });
                              }}
                            />
                            {service.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#FFF9C4', borderRadius: '5px'}}>
                      <h5 style={{margin: '0 0 10px 0'}}>💰 Dynamic Pricing</h5>
                      <label style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px'}}>
                        <input
                          type="checkbox"
                          checked={selectedLocation.hotel_data?.dynamic_pricing?.enabled || false}
                          onChange={(e) => {
                            const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: [], services: {}, dynamic_pricing: {}};
                            updateLocation(selectedLocation.id, 'hotel_data', {
                              ...hotelData,
                              dynamic_pricing: {
                                ...hotelData.dynamic_pricing,
                                enabled: e.target.checked
                              }
                            });
                          }}
                        />
                        Enable Dynamic Pricing (prices change by time/demand)
                      </label>
                      {selectedLocation.hotel_data?.dynamic_pricing?.enabled && (
                        <div style={{marginTop: '10px'}}>
                          <label style={styles.label}>
                            Peak Hours Multiplier (e.g., 1.3 = +30% price):
                            <input
                              type="number"
                              step="0.1"
                              min="1"
                              max="3"
                              value={selectedLocation.hotel_data?.dynamic_pricing?.peak_multiplier || 1.3}
                              onChange={(e) => {
                                const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: [], services: {}, dynamic_pricing: {}};
                                updateLocation(selectedLocation.id, 'hotel_data', {
                                  ...hotelData,
                                  dynamic_pricing: {
                                    ...hotelData.dynamic_pricing,
                                    peak_multiplier: parseFloat(e.target.value)
                                  }
                                });
                              }}
                              style={styles.input}
                            />
                          </label>
                          <label style={styles.label}>
                            Weekend Multiplier:
                            <input
                              type="number"
                              step="0.1"
                              min="1"
                              max="2"
                              value={selectedLocation.hotel_data?.dynamic_pricing?.weekend_multiplier || 1.2}
                              onChange={(e) => {
                                const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: [], services: {}, dynamic_pricing: {}};
                                updateLocation(selectedLocation.id, 'hotel_data', {
                                  ...hotelData,
                                  dynamic_pricing: {
                                    ...hotelData.dynamic_pricing,
                                    weekend_multiplier: parseFloat(e.target.value)
                                  }
                                });
                              }}
                              style={styles.input}
                            />
                          </label>
                          <small style={{color: '#666', fontSize: '12px'}}>
                            💡 Players will see higher prices during peak times (evenings/weekends)
                          </small>
                        </div>
                      )}
                    </div>

                    <label style={styles.label}>
                      Amenities (comma separated):
                      <input
                        type="text"
                        value={(selectedLocation.hotel_data?.amenities || []).join(', ')}
                        onChange={(e) => {
                          const hotelData = selectedLocation.hotel_data || {rooms: []};
                          updateLocation(selectedLocation.id, 'hotel_data', {
                            ...hotelData,
                            amenities: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                          });
                        }}
                        style={styles.input}
                        placeholder="e.g., WiFi, Pool, Gym, Restaurant, Spa"
                      />
                    </label>

                    <h5 style={{marginTop: '20px'}}>Room Management</h5>
                    {(selectedLocation.hotel_data?.rooms || []).map((room, idx) => (
                      <div key={idx} style={{
                        padding: '15px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        border: '1px solid #ddd'
                      }}>
                        <label style={styles.label}>
                          Room Type:
                          <input
                            type="text"
                            value={room.type}
                            onChange={(e) => {
                              const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: []};
                              const newRooms = [...hotelData.rooms];
                              newRooms[idx] = {...room, type: e.target.value};
                              updateLocation(selectedLocation.id, 'hotel_data', {...hotelData, rooms: newRooms});
                            }}
                            style={styles.input}
                            placeholder="e.g., Standard Room"
                          />
                        </label>
                        <div style={{display: 'flex', gap: '10px'}}>
                          <label style={{...styles.label, flex: 1}}>
                            Price (gems):
                            <input
                              type="number"
                              value={room.price}
                              onChange={(e) => {
                                const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: []};
                                const newRooms = [...hotelData.rooms];
                                newRooms[idx] = {...room, price: parseInt(e.target.value)};
                                updateLocation(selectedLocation.id, 'hotel_data', {...hotelData, rooms: newRooms});
                              }}
                              style={styles.input}
                            />
                          </label>
                          <label style={{...styles.label, flex: 1}}>
                            Available:
                            <input
                              type="number"
                              value={room.available}
                              onChange={(e) => {
                                const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: []};
                                const newRooms = [...hotelData.rooms];
                                newRooms[idx] = {...room, available: parseInt(e.target.value)};
                                updateLocation(selectedLocation.id, 'hotel_data', {...hotelData, rooms: newRooms});
                              }}
                              style={styles.input}
                            />
                          </label>
                        </div>
                        <label style={styles.label}>
                          Description:
                          <input
                            type="text"
                            value={room.description || ''}
                            onChange={(e) => {
                              const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: []};
                              const newRooms = [...hotelData.rooms];
                              newRooms[idx] = {...room, description: e.target.value};
                              updateLocation(selectedLocation.id, 'hotel_data', {...hotelData, rooms: newRooms});
                            }}
                            style={styles.input}
                            placeholder="e.g., Comfortable room with city view"
                          />
                        </label>
                        <button
                          onClick={() => {
                            const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: []};
                            const newRooms = hotelData.rooms.filter((_, i) => i !== idx);
                            updateLocation(selectedLocation.id, 'hotel_data', {...hotelData, rooms: newRooms});
                          }}
                          style={{...styles.deleteButton, marginTop: '10px'}}
                        >
                          Remove Room
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const hotelData = selectedLocation.hotel_data || {rooms: [], amenities: []};
                        const newRoom = {
                          id: `room_${Date.now()}`,
                          type: 'Standard Room',
                          price: 500,
                          available: 5,
                          description: ''
                        };
                        updateLocation(selectedLocation.id, 'hotel_data', {
                          ...hotelData,
                          rooms: [...hotelData.rooms, newRoom]
                        });
                      }}
                      style={{...styles.addBtn, marginTop: '10px'}}
                    >
                      + Add Room
                    </button>
                  </div>
                )}

                {/* RESTAURANT CONFIGURATION */}
                {selectedLocation.location_type === 'restaurant' && (
                  <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#FFF3E0', borderRadius: '8px'}}>
                    <h4>🍽️ Restaurant Configuration</h4>
                    
                    <label style={styles.label}>
                      Waiter Character:
                      <select
                        value={selectedLocation.restaurant_data?.waiter_character_id || ''}
                        onChange={(e) => {
                          const restaurantData = selectedLocation.restaurant_data || {menu: [], dynamic_pricing: {}};
                          updateLocation(selectedLocation.id, 'restaurant_data', {
                            ...restaurantData,
                            waiter_character_id: e.target.value || null
                          });
                        }}
                        style={styles.input}
                      >
                        <option value="">-- No Character --</option>
                        {characters.map(char => (
                          <option key={char.id} value={char.id}>{char.emoji} {char.name}</option>
                        ))}
                      </select>
                      <small style={{color: '#666', fontSize: '12px'}}>
                        💡 Select waiter for "Speak to Waiter" feature
                      </small>
                    </label>

                    <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#FFF9C4', borderRadius: '5px'}}>
                      <h5 style={{margin: '0 0 10px 0'}}>💰 Dynamic Pricing</h5>
                      <label style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px'}}>
                        <input
                          type="checkbox"
                          checked={selectedLocation.restaurant_data?.dynamic_pricing?.enabled || false}
                          onChange={(e) => {
                            const restaurantData = selectedLocation.restaurant_data || {menu: [], dynamic_pricing: {}};
                            updateLocation(selectedLocation.id, 'restaurant_data', {
                              ...restaurantData,
                              dynamic_pricing: {
                                ...restaurantData.dynamic_pricing,
                                enabled: e.target.checked
                              }
                            });
                          }}
                        />
                        Enable Dynamic Pricing (prices change by meal time)
                      </label>
                      {selectedLocation.restaurant_data?.dynamic_pricing?.enabled && (
                        <div style={{marginTop: '10px'}}>
                          <label style={styles.label}>
                            Lunch Discount (e.g., 0.8 = -20% price):
                            <input
                              type="number"
                              step="0.1"
                              min="0.5"
                              max="1"
                              value={selectedLocation.restaurant_data?.dynamic_pricing?.lunch_discount || 0.8}
                              onChange={(e) => {
                                const restaurantData = selectedLocation.restaurant_data || {menu: [], dynamic_pricing: {}};
                                updateLocation(selectedLocation.id, 'restaurant_data', {
                                  ...restaurantData,
                                  dynamic_pricing: {
                                    ...restaurantData.dynamic_pricing,
                                    lunch_discount: parseFloat(e.target.value)
                                  }
                                });
                              }}
                              style={styles.input}
                            />
                          </label>
                          <label style={styles.label}>
                            Dinner Premium (e.g., 1.2 = +20% price):
                            <input
                              type="number"
                              step="0.1"
                              min="1"
                              max="2"
                              value={selectedLocation.restaurant_data?.dynamic_pricing?.dinner_premium || 1.2}
                              onChange={(e) => {
                                const restaurantData = selectedLocation.restaurant_data || {menu: [], dynamic_pricing: {}};
                                updateLocation(selectedLocation.id, 'restaurant_data', {
                                  ...restaurantData,
                                  dynamic_pricing: {
                                    ...restaurantData.dynamic_pricing,
                                    dinner_premium: parseFloat(e.target.value)
                                  }
                                });
                              }}
                              style={styles.input}
                            />
                          </label>
                          <small style={{color: '#666', fontSize: '12px'}}>
                            💡 Lunch (12-2pm) cheaper, Dinner (7-9pm) more expensive
                          </small>
                        </div>
                      )}
                    </div>

                    <h5 style={{marginTop: '20px'}}>Menu Management</h5>
                    {(selectedLocation.restaurant_data?.menu || []).map((item, idx) => (
                      <div key={idx} style={{
                        padding: '15px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        border: '1px solid #ddd'
                      }}>
                        <label style={styles.label}>
                          Dish Name:
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => {
                              const restaurantData = selectedLocation.restaurant_data || {menu: []};
                              const newMenu = [...restaurantData.menu];
                              newMenu[idx] = {...item, name: e.target.value};
                              updateLocation(selectedLocation.id, 'restaurant_data', {...restaurantData, menu: newMenu});
                            }}
                            style={styles.input}
                            placeholder="e.g., Tagine"
                          />
                        </label>
                        <div style={{display: 'flex', gap: '10px'}}>
                          <label style={{...styles.label, flex: 1}}>
                            Price (gems):
                            <input
                              type="number"
                              value={item.price}
                              onChange={(e) => {
                                const restaurantData = selectedLocation.restaurant_data || {menu: []};
                                const newMenu = [...restaurantData.menu];
                                newMenu[idx] = {...item, price: parseInt(e.target.value)};
                                updateLocation(selectedLocation.id, 'restaurant_data', {...restaurantData, menu: newMenu});
                              }}
                              style={styles.input}
                            />
                          </label>
                          <label style={{...styles.label, flex: 1}}>
                            Energy Bonus:
                            <input
                              type="number"
                              value={item.energy_bonus || 10}
                              onChange={(e) => {
                                const restaurantData = selectedLocation.restaurant_data || {menu: []};
                                const newMenu = [...restaurantData.menu];
                                newMenu[idx] = {...item, energy_bonus: parseInt(e.target.value)};
                                updateLocation(selectedLocation.id, 'restaurant_data', {...restaurantData, menu: newMenu});
                              }}
                              style={styles.input}
                            />
                          </label>
                        </div>
                        <label style={styles.label}>
                          Description:
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => {
                              const restaurantData = selectedLocation.restaurant_data || {menu: []};
                              const newMenu = [...restaurantData.menu];
                              newMenu[idx] = {...item, description: e.target.value};
                              updateLocation(selectedLocation.id, 'restaurant_data', {...restaurantData, menu: newMenu});
                            }}
                            style={styles.input}
                            placeholder="e.g., Traditional Moroccan stew"
                          />
                        </label>
                        <button
                          onClick={() => {
                            const restaurantData = selectedLocation.restaurant_data || {menu: []};
                            const newMenu = restaurantData.menu.filter((_, i) => i !== idx);
                            updateLocation(selectedLocation.id, 'restaurant_data', {...restaurantData, menu: newMenu});
                          }}
                          style={{...styles.deleteButton, marginTop: '10px'}}
                        >
                          Remove Item
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const restaurantData = selectedLocation.restaurant_data || {menu: []};
                        const newItem = {
                          id: `food_${Date.now()}`,
                          name: 'New Dish',
                          price: 150,
                          energy_bonus: 10,
                          description: '',
                          available: true
                        };
                        updateLocation(selectedLocation.id, 'restaurant_data', {
                          ...restaurantData,
                          menu: [...restaurantData.menu, newItem]
                        });
                      }}
                      style={{...styles.addBtn, marginTop: '10px'}}
                    >
                      + Add Menu Item
                    </button>
                  </div>
                )}


                {/* GYM CONFIGURATION */}
                {selectedLocation.location_type === 'gym' && (
                  <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#E8F5E9', borderRadius: '8px'}}>
                    <h4>💪 Gym Configuration</h4>
                    
                    <label style={styles.label}>
                      Trainer Character:
                      <select
                        value={selectedLocation.gym_data?.trainer_character_id || ''}
                        onChange={(e) => {
                          const gymData = selectedLocation.gym_data || {membership_price: 200, daily_pass_price: 50, energy_cost: 30};
                          updateLocation(selectedLocation.id, 'gym_data', {
                            ...gymData,
                            trainer_character_id: e.target.value || null
                          });
                        }}
                        style={styles.input}
                      >
                        <option value="">-- No Character --</option>
                        {characters.map(char => (
                          <option key={char.id} value={char.id}>{char.emoji} {char.name}</option>
                        ))}
                      </select>
                      <small style={{color: '#666', fontSize: '12px'}}>
                        💡 Select trainer for "Speak to Trainer" feature
                      </small>
                    </label>

                    <div style={{display: 'flex', gap: '10px'}}>
                      <label style={{...styles.label, flex: 1}}>
                        Membership Price (gems):
                        <input
                          type="number"
                          value={selectedLocation.gym_data?.membership_price || 200}
                          onChange={(e) => {
                            const gymData = selectedLocation.gym_data || {daily_pass_price: 50, energy_cost: 30};
                            updateLocation(selectedLocation.id, 'gym_data', {
                              ...gymData,
                              membership_price: parseInt(e.target.value)
                            });
                          }}
                          style={styles.input}
                        />
                      </label>
                      <label style={{...styles.label, flex: 1}}>
                        Daily Pass Price (gems):
                        <input
                          type="number"
                          value={selectedLocation.gym_data?.daily_pass_price || 50}
                          onChange={(e) => {
                            const gymData = selectedLocation.gym_data || {membership_price: 200, energy_cost: 30};
                            updateLocation(selectedLocation.id, 'gym_data', {
                              ...gymData,
                              daily_pass_price: parseInt(e.target.value)
                            });
                          }}
                          style={styles.input}
                        />
                      </label>
                    </div>

                    <label style={styles.label}>
                      Energy Cost Per Workout:
                      <input
                        type="number"
                        value={selectedLocation.gym_data?.energy_cost || 30}
                        onChange={(e) => {
                          const gymData = selectedLocation.gym_data || {membership_price: 200, daily_pass_price: 50};
                          updateLocation(selectedLocation.id, 'gym_data', {
                            ...gymData,
                            energy_cost: parseInt(e.target.value)
                          });
                        }}
                        style={styles.input}
                      />
                      <small style={{color: '#666', fontSize: '12px'}}>
                        💡 Working out costs energy (-30% typical)
                      </small>
                    </label>
                  </div>
                )}

              </div>
              )}

              <div style={styles.actionButtons}>
                <button onClick={() => deleteLocation(selectedLocation.id)} style={styles.deleteButton}>
                  🗑️ Delete Location
                </button>
                <button onClick={saveGameData} style={styles.saveButton}>
                  💾 Save Changes
                </button>
              </div>
            </>
          ) : (
            <div style={styles.emptyEditor}>
              <p>👈 Select a location from the list to edit</p>
              <p>or click "+ Add Location" to create a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Characters Tab Component
function CharactersTab({ characters, selectedCharacterId, setSelectedCharacterId, addCharacter, updateCharacter, deleteCharacter, saveGameData }) {
  const selectedCharacter = characters.find(char => char.id === selectedCharacterId);

  return (
    <div style={styles.tabContent}>
      <div style={styles.tabHeader}>
        <h2>👥 Character Management</h2>
        <button onClick={addCharacter} style={styles.addBtn}>
          + Add Character
        </button>
      </div>

      <div style={styles.splitLayout}>
        {/* Character List */}
        <div style={styles.listPanel}>
          <h3 style={styles.panelTitle}>Characters</h3>
          {characters.map(char => (
            <div
              key={char.id}
              onClick={() => setSelectedCharacterId(char.id)}
              style={{
                ...styles.listItem,
                ...(selectedCharacterId === char.id ? styles.selectedItem : {})
              }}
            >
              <span style={{fontSize: '24px', marginRight: '10px'}}>{char.emoji}</span>
              <span style={{fontWeight: 'bold'}}>{char.name}</span>
              {char.is_translator && <span style={styles.translatorBadge}>Translator</span>}
              {selectedCharacterId === char.id && (
                <span style={styles.highlightBadge}>Selected</span>
              )}
            </div>
          ))}
          {characters.length === 0 && (
            <div style={styles.emptyState}>No characters yet</div>
          )}
        </div>

        {/* Character Editor */}
        <div style={styles.editorPanel}>
          {selectedCharacter ? (
            <>
              <h3 style={styles.panelTitle}>Edit Character: {selectedCharacter.name}</h3>
              
              <label style={styles.label}>
                Character Name:
                <input
                  type="text"
                  value={selectedCharacter.name}
                  onChange={(e) => updateCharacter(selectedCharacter.id, 'name', e.target.value)}
                  style={styles.input}
                />
              </label>

              <label style={styles.label}>
                Emoji:
                <input
                  type="text"
                  value={selectedCharacter.emoji}
                  onChange={(e) => updateCharacter(selectedCharacter.id, 'emoji', e.target.value)}
                  style={styles.inputSmall}
                  maxLength="2"
                />
              </label>

              <div style={{marginBottom: '15px'}}>
                <label style={styles.label}>Custom Character Image:</label>
                <div style={{display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '5px'}}>
                  <div style={{flex: 1}}>
                    <input
                      type="text"
                      value={selectedCharacter.image_url || ''}
                      onChange={(e) => updateCharacter(selectedCharacter.id, 'image_url', e.target.value)}
                      style={styles.input}
                      placeholder="Option 2: Or paste Dropbox/URL"
                    />
                  </div>
                  
                  <label 
                    htmlFor={`char-upload-${selectedCharacter.id}`}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    📤 Upload
                  </label>
                  <input
                    id={`char-upload-${selectedCharacter.id}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          alert('⚠️ Image too large! Max 5MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          updateCharacter(selectedCharacter.id, 'image_url', event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    style={{display: 'none'}}
                  />
                </div>
                <p style={{fontSize: '11px', color: '#666', margin: '5px 0 0 0'}}>
                  💡 Option 1: Upload from computer (Max 5MB) | Option 2: Paste URL
                </p>
              </div>

              {selectedCharacter.image_url && (
                <div style={styles.imagePreview}>
                  <p style={{fontSize: '12px', color: '#666', margin: '5px 0'}}>Preview:</p>
                  <img 
                    src={selectedCharacter.image_url} 
                    alt="Character preview"
                    style={{maxWidth: '100px', maxHeight: '100px', borderRadius: '4px'}}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <p style={{display: 'none', color: '#f44336', fontSize: '12px'}}>❌ Failed to load image</p>
                </div>
              )}

              <div style={styles.controlsSection}>
                <h4>Position & Appearance</h4>
                
                <label style={styles.label}>
                  X-Axis (Left ← → Right):
                  <input
                    type="range"
                    min="0"
                    max="3000"
                    value={selectedCharacter.position.x}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'position', {...selectedCharacter.position, x: parseInt(e.target.value)})}
                    style={styles.slider}
                  />
                  <span>{selectedCharacter.position.x}px</span>
                </label>

                <label style={styles.label}>
                  Y-Axis (Top ↑ ↓ Down):
                  <input
                    type="range"
                    min="0"
                    max="2000"
                    value={selectedCharacter.position.y}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'position', {...selectedCharacter.position, y: parseInt(e.target.value)})}
                    style={styles.slider}
                  />
                  <span>{selectedCharacter.position.y}px</span>
                </label>

                <label style={styles.label}>
                  Z-Axis (Back ↗ ↙ Front):
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={selectedCharacter.position.z}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'position', {...selectedCharacter.position, z: parseInt(e.target.value)})}
                    style={styles.slider}
                  />
                  <span>{selectedCharacter.position.z}px</span>
                </label>

                <label style={styles.label}>
                  Size:
                  <input
                    type="range"
                    min="40"
                    max="150"
                    value={selectedCharacter.size}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'size', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedCharacter.size}px</span>
                </label>

                <label style={styles.label}>
                  Manual Rotation:
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={selectedCharacter.rotation || 0}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'rotation', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedCharacter.rotation || 0}°</span>
                </label>

                <div style={styles.checkboxGroup}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={selectedCharacter.flipH || false}
                      onChange={(e) => updateCharacter(selectedCharacter.id, 'flipH', e.target.checked)}
                    />
                    Flip Horizontal
                  </label>

                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={selectedCharacter.flipV || false}
                      onChange={(e) => updateCharacter(selectedCharacter.id, 'flipV', e.target.checked)}
                    />
                    Flip Vertical (Mirror)
                  </label>
                </div>
              </div>

              <div style={styles.controlsSection}>
                <h4>Character Role</h4>
                
                <label style={styles.label}>
                  Role Type:
                  <select
                    value={selectedCharacter.role || 'character'}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'role', e.target.value)}
                    style={styles.input}
                  >
                    <option value="character">Regular Character</option>
                    <option value="translator">Translator (Helper)</option>
                    <option value="quest_giver">Quest Giver</option>
                    <option value="vendor">Vendor/Shop Owner</option>
                  </select>
                </label>

                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={selectedCharacter.is_translator}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'is_translator', e.target.checked)}
                  />
                  Mark as Translator (overrides role)
                </label>

                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={selectedCharacter.is_walking || false}
                    onChange={(e) => updateCharacter(selectedCharacter.id, 'is_walking', e.target.checked)}
                  />
                  Character Walks/Moves (enable path markers)
                </label>
              </div>

              {selectedCharacter.is_walking && (
                <div style={styles.controlsSection}>
                  <h4>🚶 Walking Path Markers</h4>
                  <p style={{fontSize: '12px', color: '#666', marginBottom: '10px'}}>
                    Set multiple points for this character to walk between
                  </p>
                  
                  <button 
                    onClick={() => {
                      const newMarker = {
                        id: `marker_${Date.now()}`,
                        x: selectedCharacter.position.x,
                        y: selectedCharacter.position.y,
                        name: `Point ${(selectedCharacter.path_markers?.length || 0) + 1}`
                      };
                      const markers = [...(selectedCharacter.path_markers || []), newMarker];
                      updateCharacter(selectedCharacter.id, 'path_markers', markers);
                    }}
                    style={styles.addBtn}
                  >
                    + Add Marker at Current Position
                  </button>

                  <div style={{marginTop: '15px'}}>
                    {(selectedCharacter.path_markers || []).map((marker, idx) => (
                      <div key={marker.id} style={styles.markerCard}>
                        <div style={styles.markerHeader}>
                          <span style={{fontWeight: 'bold'}}>{marker.name}</span>
                          <button
                            onClick={() => {
                              const markers = selectedCharacter.path_markers.filter(m => m.id !== marker.id);
                              updateCharacter(selectedCharacter.id, 'path_markers', markers);
                            }}
                            style={styles.deleteBtn}
                          >
                            🗑️
                          </button>
                        </div>
                        <div style={styles.positionDisplay}>
                          <span>X: {marker.x}px</span>
                          <span>Y: {marker.y}px</span>
                        </div>
                      </div>
                    ))}
                    {(!selectedCharacter.path_markers || selectedCharacter.path_markers.length === 0) && (
                      <p style={{fontSize: '12px', color: '#999'}}>No markers yet. Position character and click "Add Marker"</p>
                    )}
                  </div>
                </div>
              )}

              <div style={{padding: '15px', backgroundColor: '#E3F2FD', borderRadius: '6px', marginTop: '20px'}}>
                <h4 style={{margin: '0 0 10px 0'}}>💡 Live Preview</h4>
                <p style={{fontSize: '13px', color: '#666', margin: 0}}>
                  Open the game in a new tab to see this character highlighted in real-time as you adjust sliders.
                  <br />
                  <a href="/game" target="_blank" style={{color: '#5C6BC0', fontWeight: 'bold'}}>
                    → Open Game Preview
                  </a>
                </p>
              </div>

              <div style={styles.actionButtons}>
                <button onClick={() => deleteCharacter(selectedCharacter.id)} style={styles.deleteButton}>
                  🗑️ Delete Character
                </button>
                <button onClick={saveGameData} style={styles.saveButton}>
                  💾 Save Changes
                </button>
              </div>
            </>
          ) : (
            <div style={styles.emptyEditor}>
              <p>👈 Select a character from the list to edit</p>
              <p>or click "+ Add Character" to create a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Taxi & Markers Tab Component
function TaxiMarkersTab({ taxiSettings, updateTaxiSettings, updateTaxiWithoutPreview, markers, setMarkers, routes, setRoutes, selectedRouteId, setSelectedRouteId, roads, setRoads, selectedRoadId, setSelectedRoadId, roadNodes, setRoadNodes, placingNodes, setPlacingNodes, characters, locations, saveGameData, previewWindow, broadcastToPreview }) {
  const [playingRouteId, setPlayingRouteId] = useState(null); // Track which specific route is playing
  const [expandedMarkers, setExpandedMarkers] = useState({}); // Track which markers are expanded
  const [newRouteName, setNewRouteName] = useState(''); // For creating new route
  const [uploadingTaxiImage, setUploadingTaxiImage] = useState(false);
  
  // updateTaxiWithoutPreview is passed as prop from parent - no need to redefine
  
  const addMarker = () => {
    const newMarker = {
      id: `marker_${Date.now()}`,
      name: `Stop ${markers.length + 1}`,
      position: { ...taxiSettings.position },
      rotation: taxiSettings.rotation,
      animation_speed: 3, // Default: Normal speed (3 seconds)
      note: ''
    };
    const newMarkers = [...markers, newMarker];
    setMarkers(newMarkers);
    // NO auto-save, NO preview, NO localStorage - only manual save to database
  };

  const updateMarker = (id, field, value) => {
    const updatedMarkers = markers.map(m => m.id === id ? { ...m, [field]: value } : m);
    setMarkers(updatedMarkers);
    // NO auto-save, NO preview, NO localStorage - only manual save to database
  };

  const deleteMarker = (id) => {
    console.log('🗑️ Deleting marker:', id);
    const updatedMarkers = markers.filter(m => m.id !== id);
    setMarkers(updatedMarkers);
    
    // Force broadcast to preview after state update
    setTimeout(() => {
      if (previewWindow && !previewWindow.closed) {
        console.log('📤 Force broadcasting after marker deletion');
        broadcastToPreview();
      }
    }, 150);
  };
  
  // Route Group Management
  const addRoute = () => {
    if (!newRouteName.trim() || !newRouteName.includes(' → ')) {
      return; // Just do nothing if empty or invalid format
    }
    
    // Parse location names from route name "Airport → Restaurant"
    const [startName, endName] = newRouteName.split(' → ').map(s => s.trim());
    
    // Find actual location IDs
    const startLoc = locations.find(loc => loc.name === startName);
    const endLoc = locations.find(loc => loc.name === endName);
    
    if (!startLoc || !endLoc) {
      console.error('Could not find locations for route:', startName, endName);
      return;
    }
    
    const newRoute = {
      id: `route_${Date.now()}`,
      name: newRouteName,
      marker_ids: [], // Empty array to hold marker IDs
      start_location: startLoc.id, // Store location ID
      end_location: endLoc.id // Store location ID
    };
    
    console.log('✅ Created route:', newRoute);
    setRoutes([...routes, newRoute]);
    setSelectedRouteId(newRoute.id);
    setNewRouteName('');
  };
  
  const deleteRoute = (id) => {
    setRoutes(routes.filter(r => r.id !== id));
    if (selectedRouteId === id) {
      setSelectedRouteId(null);
    }
  };
  
  const addMarkerToRoute = () => {
    if (!selectedRouteId) {
      return; // Just do nothing if no route selected
    }
    
    const newMarker = {
      id: `marker_${Date.now()}`,
      route_id: selectedRouteId, // Link marker to route
      name: `Stop ${markers.filter(m => m.route_id === selectedRouteId).length + 1}`,
      position: { ...taxiSettings.position },
      rotation: taxiSettings.rotation,
      animation_speed: 3,
      note: '',
      is_start: false, // Default: not a start marker
      is_final: false // Default: not a final marker
    };
    
    const newMarkers = [...markers, newMarker];
    setMarkers(newMarkers);
    // NO auto-save, NO preview, NO localStorage - only manual save to database
  };
  
  // Get markers for selected route
  const getRouteMarkers = (routeId) => {
    return markers.filter(m => m.route_id === routeId);
  };
  
  // Handle taxi image upload
  const handleTaxiImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ Image too large! Please use an image under 5MB.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('⚠️ Please upload an image file');
      return;
    }

    setUploadingTaxiImage(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target.result;
      updateTaxiWithoutPreview({image_url: dataUrl});
      setUploadingTaxiImage(false);
    };
    
    reader.onerror = () => {
      setUploadingTaxiImage(false);
      alert('❌ Failed to read image');
    };
    
    reader.readAsDataURL(file);
  };
  
  const playTaxiAnimation = async (routeId = null) => {
    // Get markers for specified route, or fall back to selected route, or all markers
    const targetRouteId = routeId || selectedRouteId;
    const routeMarkers = targetRouteId 
      ? markers.filter(m => m.route_id === targetRouteId)
      : markers;
    
    if (routeMarkers.length === 0) {
      return; // No markers to preview
    }
    
    setPlayingRouteId(targetRouteId); // Mark THIS specific route as playing
    
    // Move taxi to FIRST marker position (starting point)
    const firstMarker = routeMarkers[0];
    updateTaxiWithoutPreview({
      position: { ...firstMarker.position },
      rotation: firstMarker.rotation || 0
    });
    
    // Wait a moment to show starting position
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Animate through remaining markers
    for (let i = 1; i < routeMarkers.length; i++) {
      const marker = routeMarkers[i];
      
      // Update taxi position
      updateTaxiWithoutPreview({
        position: { ...marker.position },
        rotation: marker.rotation || 0
      });
      
      // Wait based on marker's animation speed
      const waitTime = (marker.animation_speed || 3) * 1000;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    setPlayingRouteId(null); // Clear playing state when done
  };

  return (
    <div style={styles.tabContent}>
      <h2>🚕 Taxi Settings & Path Markers</h2>
      
      {/* Taxi Settings */}
      <div style={styles.section}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h3 style={styles.sectionTitle}>🚕 Taxi Configuration</h3>
          <div style={{
            ...styles.highlightIndicator,
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
          }}>
            <span style={{fontSize: '20px', marginRight: '8px'}}>🚕</span>
            <span style={{fontWeight: 'bold'}}>WORKING ON TAXI</span>
          </div>
        </div>
        
        <div style={styles.controlGrid}>
          <label style={styles.label}>
            X-Axis (Left ← → Right):
            <input
              type="range"
              min="0"
              max="1200"
              value={taxiSettings.position.x}
              onChange={(e) => updateTaxiWithoutPreview({position: {...taxiSettings.position, x: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{taxiSettings.position.x}px</span>
          </label>

          <label style={styles.label}>
            Y-Axis (Top ↑ ↓ Down):
            <input
              type="range"
              min="0"
              max="800"
              value={taxiSettings.position.y}
              onChange={(e) => updateTaxiWithoutPreview({position: {...taxiSettings.position, y: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{taxiSettings.position.y}px</span>
          </label>

          <label style={styles.label}>
            Z-Axis (Back ↗ ↙ Front):
            <input
              type="range"
              min="0"
              max="200"
              value={taxiSettings.position.z}
              onChange={(e) => updateTaxiWithoutPreview({position: {...taxiSettings.position, z: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{taxiSettings.position.z}px</span>
          </label>

          <label style={styles.label}>
            Size:
            <input
              type="range"
              min="30"
              max="150"
              value={taxiSettings.size}
              onChange={(e) => updateTaxiWithoutPreview({size: parseInt(e.target.value)})}
              style={styles.slider}
            />
            <span>{taxiSettings.size}px</span>
          </label>

          <label style={styles.label}>
            Manual Rotation:
            <input
              type="range"
              min="0"
              max="360"
              value={taxiSettings.rotation}
              onChange={(e) => updateTaxiWithoutPreview({rotation: parseInt(e.target.value)})}
              style={styles.slider}
            />
            <span>{taxiSettings.rotation}°</span>
          </label>
          
          {/* Taxi Direction Compass */}
          <div style={{
            backgroundColor: '#E3F2FD',
            padding: '15px',
            borderRadius: '8px',
            marginTop: '10px',
            marginBottom: '10px',
            textAlign: 'center'
          }}>
            <div style={{fontWeight: 'bold', marginBottom: '10px', color: '#1565C0'}}>
              🧭 Taxi Direction Compass
            </div>
            <div style={{
              position: 'relative',
              width: '120px',
              height: '120px',
              margin: '0 auto',
              border: '3px solid #1565C0',
              borderRadius: '50%',
              backgroundColor: 'white'
            }}>
              {/* N/S/E/W Labels */}
              <span style={{position: 'absolute', top: '5px', left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold', color: '#f44336'}}>N</span>
              <span style={{position: 'absolute', bottom: '5px', left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold'}}>S</span>
              <span style={{position: 'absolute', left: '5px', top: '50%', transform: 'translateY(-50%)', fontWeight: 'bold'}}>W</span>
              <span style={{position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', fontWeight: 'bold'}}>E</span>
              
              {/* Taxi Arrow */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) rotate(${taxiSettings.rotation}deg)`,
                fontSize: '30px',
                lineHeight: 1
              }}>
                🚕
              </div>
              
              {/* Direction Arrow */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '40px',
                height: '4px',
                backgroundColor: '#f44336',
                transformOrigin: '0 50%',
                transform: `translate(0, -50%) rotate(${taxiSettings.rotation - 90}deg)`
              }}>
                <div style={{
                  position: 'absolute',
                  right: '-8px',
                  top: '-5px',
                  width: 0,
                  height: 0,
                  borderLeft: '12px solid #f44336',
                  borderTop: '7px solid transparent',
                  borderBottom: '7px solid transparent'
                }}></div>
              </div>
            </div>
            <div style={{marginTop: '10px', fontSize: '12px', color: '#666'}}>
              {taxiSettings.rotation === 0 ? '⬆️ Facing North (Up)' :
               taxiSettings.rotation === 90 ? '➡️ Facing East (Right)' :
               taxiSettings.rotation === 180 ? '⬇️ Facing South (Down)' :
               taxiSettings.rotation === 270 ? '⬅️ Facing West (Left)' :
               `↗️ Facing ${taxiSettings.rotation}°`}
            </div>
            <div style={{display: 'flex', gap: '5px', justifyContent: 'center', marginTop: '8px'}}>
              <button onClick={() => updateTaxiWithoutPreview({rotation: 0})} style={{padding: '4px 8px', fontSize: '11px', cursor: 'pointer'}}>⬆️ N</button>
              <button onClick={() => updateTaxiWithoutPreview({rotation: 90})} style={{padding: '4px 8px', fontSize: '11px', cursor: 'pointer'}}>➡️ E</button>
              <button onClick={() => updateTaxiWithoutPreview({rotation: 180})} style={{padding: '4px 8px', fontSize: '11px', cursor: 'pointer'}}>⬇️ S</button>
              <button onClick={() => updateTaxiWithoutPreview({rotation: 270})} style={{padding: '4px 8px', fontSize: '11px', cursor: 'pointer'}}>⬅️ W</button>
            </div>
          </div>

          <label style={styles.label}>
            Animation Speed (Global for all movements):
            <input
              type="range"
              min="0.001"
              max="5"
              step="0.001"
              value={taxiSettings.animationSpeed}
              onChange={(e) => updateTaxiWithoutPreview({animationSpeed: parseFloat(e.target.value)})}
              style={styles.slider}
            />
            <span>
              {taxiSettings.animationSpeed < 0.01 ? '⚡ Ultra Fast' : 
               taxiSettings.animationSpeed < 0.05 ? '🚀 Very Fast' :
               taxiSettings.animationSpeed < 0.1 ? '🏃 Fast' :
               taxiSettings.animationSpeed < 0.5 ? '🚶 Medium' :
               taxiSettings.animationSpeed < 1 ? '🐢 Slow' :
               taxiSettings.animationSpeed < 2 ? '🐌 Very Slow' :
               '🐌 Ultra Slow'} ({taxiSettings.animationSpeed}s = {Math.round(taxiSettings.animationSpeed * 1000)}ms)
            </span>
          </label>
        </div>

        <div style={styles.checkboxGroup}>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={taxiSettings.flipH}
              onChange={(e) => updateTaxiWithoutPreview({flipH: e.target.checked})}
            />
            Flip Horizontal
          </label>

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={taxiSettings.flipV}
              onChange={(e) => updateTaxiWithoutPreview({flipV: e.target.checked})}
            />
            Flip Vertical
          </label>
        </div>

        <label style={styles.label}>
          Taxi Style:
          <select
            value={taxiSettings.style}
            onChange={(e) => updateTaxiWithoutPreview({style: e.target.value})}
            style={styles.input}
          >
            <option value="classic">🚕 Classic Taxi</option>
            <option value="modern">🚗 Modern Car</option>
            <option value="van">🚐 Van</option>
          </select>
        </label>

        <div style={{marginBottom: '15px'}}>
          <label style={styles.label}>Custom Taxi Image:</label>
          <div style={{display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '5px'}}>
            <div style={{flex: 1}}>
              <input
                type="text"
                value={taxiSettings.image_url || ''}
                onChange={(e) => updateTaxiWithoutPreview({image_url: e.target.value})}
                style={styles.input}
                placeholder="Option 2: Or paste Dropbox/URL"
              />
            </div>
            
            <label 
              htmlFor="taxi-image-upload"
              style={{
                padding: '10px 20px',
                backgroundColor: uploadingTaxiImage ? '#9E9E9E' : '#4CAF50',
                color: 'white',
                borderRadius: '6px',
                cursor: uploadingTaxiImage ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              {uploadingTaxiImage ? '⏳ Loading...' : '📤 Upload'}
            </label>
            <input
              id="taxi-image-upload"
              type="file"
              accept="image/*"
              onChange={handleTaxiImageUpload}
              disabled={uploadingTaxiImage}
              style={{display: 'none'}}
            />
          </div>
          
          {taxiSettings.image_url && (
            <div style={styles.imagePreview}>
              <p style={{fontSize: '12px', color: '#666', margin: '5px 0'}}>Preview:</p>
              <img 
                src={taxiSettings.image_url} 
                alt="Taxi preview"
                style={{maxWidth: '100px', maxHeight: '100px', borderRadius: '4px'}}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <p style={{display: 'none', color: '#f44336', fontSize: '12px'}}>❌ Failed to load image</p>
            </div>
          )}
        </div>

        <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
          <button onClick={saveGameData} style={styles.saveButton}>
            💾 Save Taxi Settings
          </button>
          <button 
            onClick={playTaxiAnimation} 
            disabled={playingRouteId !== null || markers.length === 0}
            style={{
              ...styles.saveButton,
              backgroundColor: playingRouteId !== null ? '#9E9E9E' : '#2196F3',
              cursor: playingRouteId !== null || markers.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {playingRouteId !== null ? '⏸️ Playing...' : '▶️ Preview Taxi Path'}
          </button>
        </div>
      </div>

      {/* Road Network System */}
      <div style={styles.section}>
        <h3 style={{...styles.sectionTitle, color: '#5C6BC0'}}>🛣️ Road Network (NEW)</h3>
        <p style={{color: '#666', fontSize: '14px', marginBottom: '15px'}}>
          Roads connect locations. Taxi will automatically find paths between any two locations.
          Characters can be placed on roads to talk during travel.
        </p>
        
        {/* Control Buttons */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '15px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={async () => {
              if (window.confirm('This will regenerate ALL roads using the grid system. Continue?')) {
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/game/auto-generate-roads`, {
                    method: 'POST'
                  });
                  const data = await response.json();
                  if (data.success) {
                    alert(`✅ ${data.message}`);
                    // Reload config to get new roads
                    window.location.reload();
                  } else {
                    alert(`❌ Error: ${data.message}`);
                  }
                } catch (error) {
                  alert(`❌ Error: ${error.message}`);
                }
              }
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔄 Auto-Generate All Roads
          </button>
          
          <button
            onClick={() => {
              // Toggle show_road_nodes in game config
              const newValue = !gameSettings?.show_road_nodes;
              // Save to backend
              fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/game/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ show_road_nodes: newValue })
              }).then(() => {
                alert(newValue ? '✅ Road nodes are now visible' : '❌ Road nodes are now hidden');
                window.location.reload();
              });
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            👁️ Toggle Road Nodes Visibility
          </button>
        </div>
        
        {/* Manual Road Node Placement Tool */}
        <div style={{
          backgroundColor: '#FFF3E0',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '2px solid #FF9800'
        }}>
          <h4 style={{margin: '0 0 10px 0', color: '#E65100'}}>🔵 Manual Road Node Placement</h4>
          <p style={{fontSize: '12px', color: '#666', marginBottom: '10px'}}>
            Click on the preview canvas to place road nodes. Place them at road intersections.
          </p>
          
          <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px'}}>
            <button
              onClick={() => setPlacingNodes(!placingNodes)}
              style={{
                padding: '10px 20px',
                backgroundColor: placingNodes ? '#f44336' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {placingNodes ? '🛑 Stop Placing' : '🔵 Start Placing Nodes'}
            </button>
            
            <button
              onClick={() => {
                if (window.confirm('Delete ALL road nodes?')) {
                  setRoadNodes([]);
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              🗑️ Clear All Nodes
            </button>
            
            <button
              onClick={() => {
                if (roadNodes.length > 0) {
                  setRoadNodes(roadNodes.slice(0, -1));
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              ↩️ Undo Last
            </button>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            <strong>Nodes placed:</strong> {roadNodes.length}
            {placingNodes && (
              <span style={{color: '#4CAF50', marginLeft: '10px'}}>
                ✅ Click on preview canvas to add nodes
              </span>
            )}
          </div>
          
          {roadNodes.length > 0 && (
            <div style={{marginTop: '10px', maxHeight: '150px', overflowY: 'auto'}}>
              {roadNodes.map((node, idx) => (
                <span key={idx} style={{
                  display: 'inline-block',
                  backgroundColor: '#E3F2FD',
                  padding: '2px 8px',
                  margin: '2px',
                  borderRadius: '4px',
                  fontSize: '11px'
                }}>
                  #{idx + 1}: ({node.x}, {node.y})
                  <button
                    onClick={() => setRoadNodes(roadNodes.filter((_, i) => i !== idx))}
                    style={{
                      marginLeft: '5px',
                      background: 'none',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      fontSize: '11px'
                    }}
                  >✕</button>
                </span>
              ))}
            </div>
          )}
        </div>
        
        <div style={{
          backgroundColor: '#E8EAF6',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px'
        }}>
          <strong>Current Roads:</strong> {roads.length} | 
          <strong> Total Markers:</strong> {roads.reduce((sum, r) => sum + (r.markers?.length || 0), 0)} |
          <strong> Characters on Roads:</strong> {roads.reduce((sum, r) => sum + (r.characters?.length || 0), 0)}
        </div>
        
        {/* List of Roads */}
        {roads.map((road, idx) => {
          // Get location names for display
          const getLocationName = (locId) => {
            const loc = locations.find(l => l.id === locId);
            return loc?.name || loc?.bubble_text || locId;
          };
          const fromName = getLocationName(road.connects?.[0]);
          const toName = getLocationName(road.connects?.[1]);
          
          return (
          <div key={road.id} style={{
            backgroundColor: selectedRoadId === road.id ? '#E3F2FD' : 'white',
            border: selectedRoadId === road.id ? '2px solid #2196F3' : '1px solid #ddd',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '10px'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div>
                <strong style={{fontSize: '16px'}}>🛣️ {road.name}</strong>
                <div style={{color: '#666', fontSize: '12px', marginTop: '5px'}}>
                  📍 {fromName} ↔ {toName}
                </div>
                <div style={{color: '#888', fontSize: '11px'}}>
                  {road.markers?.length || 0} markers | {road.characters?.length || 0} characters
                </div>
              </div>
              <div style={{display: 'flex', gap: '8px'}}>
                <button
                  onClick={() => setSelectedRoadId(selectedRoadId === road.id ? null : road.id)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {selectedRoadId === road.id ? '▼ Close' : '▶ Edit'}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Delete road "${road.name}"?`)) {
                      setRoads(roads.filter(r => r.id !== road.id));
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  🗑️
                </button>
              </div>
            </div>
            
            {/* Expanded Road Editor */}
            {selectedRoadId === road.id && (
              <div style={{marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd'}}>
                {/* Road Direction Info */}
                <div style={{
                  backgroundColor: '#E8F5E9',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  border: '2px solid #4CAF50'
                }}>
                  <div style={{fontWeight: 'bold', marginBottom: '8px', color: '#2E7D32'}}>
                    🛣️ Road Direction
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <div style={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontWeight: 'bold'
                    }}>
                      🚀 START: {fromName}
                    </div>
                    <div style={{fontSize: '20px'}}>→→→</div>
                    <div style={{
                      backgroundColor: '#f44336',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontWeight: 'bold'
                    }}>
                      🏁 END: {toName}
                    </div>
                  </div>
                  <p style={{color: '#666', fontSize: '11px', marginTop: '8px', marginBottom: 0}}>
                    💡 Taxi travels from START to END. Add markers along the path in order (1→2→3→...).
                  </p>
                </div>
                
                {/* Road Name */}
                <label style={styles.label}>
                  Road Name:
                  <input
                    type="text"
                    value={road.name}
                    onChange={(e) => {
                      const updated = roads.map(r => r.id === road.id ? {...r, name: e.target.value} : r);
                      setRoads(updated);
                    }}
                    style={{...styles.input, marginLeft: '10px', width: '200px'}}
                  />
                </label>
                
                {/* Markers */}
                <div style={{marginTop: '15px'}}>
                  <strong>📌 Markers:</strong>
                  
                  {/* Speed Slider */}
                  <div style={{
                    backgroundColor: '#E3F2FD',
                    padding: '10px',
                    borderRadius: '6px',
                    marginTop: '10px',
                    marginBottom: '10px'
                  }}>
                    <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                      <span>⚡ Speed:</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={road.speed || 5}
                        onChange={(e) => {
                          const updated = roads.map(r => r.id === road.id ? {...r, speed: parseInt(e.target.value)} : r);
                          setRoads(updated);
                        }}
                        style={{flex: 1}}
                      />
                      <span style={{minWidth: '80px'}}>{road.speed <= 3 ? '🐢 Slow' : road.speed <= 6 ? '🚗 Normal' : '🏎️ Fast'}</span>
                    </label>
                  </div>
                  
                  {/* Test Play Button */}
                  <button
                    onClick={async () => {
                      if (!road.markers || road.markers.length === 0) {
                        alert('Add markers first!');
                        return;
                      }
                      alert(`🚕 Test Play: ${road.markers.length} markers at ${road.speed <= 3 ? 'slow' : road.speed <= 6 ? 'normal' : 'fast'} speed\n\nMarkers will be played in sequence. Watch the taxi move!`);
                      
                      // Animate through markers
                      for (let i = 0; i < road.markers.length; i++) {
                        const marker = road.markers[i];
                        updateTaxiSettings({
                          position: { x: marker.x, y: marker.y, z: 0 },
                          rotation: marker.rotation || 0
                        });
                        
                        // Speed: 1 = 2000ms, 5 = 1000ms, 10 = 200ms
                        const delay = Math.max(200, 2200 - (road.speed || 5) * 200);
                        await new Promise(resolve => setTimeout(resolve, delay));
                      }
                      alert('✅ Test complete!');
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      marginBottom: '15px',
                      backgroundColor: '#9C27B0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    ▶️ Test Play Route
                  </button>
                  
                  {/* Marker List */}
                  {road.markers?.map((marker, mIdx) => (
                    <div key={marker.id || mIdx} style={{
                      backgroundColor: marker.type === 'redlight' ? '#FFEBEE' : 
                                       marker.type === 'turn' ? '#FFF8E1' : 
                                       marker.type === 'character' ? '#E8F5E9' : '#f5f5f5',
                      padding: '12px',
                      borderRadius: '6px',
                      marginTop: '8px',
                      border: marker.type === 'redlight' ? '2px solid #f44336' : 
                              marker.type === 'turn' ? '2px solid #FF9800' : 
                              marker.type === 'character' ? '2px solid #4CAF50' : '1px solid #ddd'
                    }}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
                        <span style={{
                          fontWeight: 'bold', 
                          color: 'white',
                          backgroundColor: marker.type === 'redlight' ? '#f44336' : 
                                          marker.type === 'turn' ? '#FF9800' : 
                                          marker.type === 'character' ? '#4CAF50' : '#2196F3',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          {mIdx + 1}
                        </span>
                        
                        {/* Marker Type Selector */}
                        <select
                          value={marker.type || 'waypoint'}
                          onChange={(e) => {
                            const updatedMarkers = road.markers.map((m, i) => 
                              i === mIdx ? {...m, type: e.target.value} : m
                            );
                            const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                            setRoads(updated);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '12px'
                          }}
                        >
                          <option value="waypoint">🔵 Waypoint (auto-pass)</option>
                          <option value="redlight">🔴 Red Light (stop)</option>
                          <option value="turn">🟡 Turn Point (stop)</option>
                          <option value="character">🟢 Character (ask)</option>
                        </select>
                        
                        <span style={{color: '#666', fontSize: '11px'}}>
                          X:{marker.x} Y:{marker.y} ↻{marker.rotation}°
                        </span>
                        
                        <button
                          onClick={() => {
                            // Move taxi to this marker
                            updateTaxiSettings({
                              position: { x: marker.x, y: marker.y, z: 0 },
                              rotation: marker.rotation || 0
                            });
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px'
                          }}
                        >
                          📍 Go Here
                        </button>
                        
                        <button
                          onClick={() => {
                            const updatedMarkers = road.markers.filter((_, i) => i !== mIdx);
                            const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                            setRoads(updated);
                          }}
                          style={{
                            marginLeft: 'auto',
                            padding: '4px 8px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      
                      {/* Message field for non-waypoint markers */}
                      {marker.type && marker.type !== 'waypoint' && (
                        <div style={{marginTop: '8px'}}>
                          <input
                            type="text"
                            placeholder={
                              marker.type === 'redlight' ? 'e.g., وقف فالضو الحمر (Stop at red light)' :
                              marker.type === 'turn' ? 'e.g., دور على اليمين (Turn right)' :
                              'e.g., Character greeting message'
                            }
                            value={marker.message || ''}
                            onChange={(e) => {
                              const updatedMarkers = road.markers.map((m, i) => 
                                i === mIdx ? {...m, message: e.target.value} : m
                              );
                              const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                              setRoads(updated);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '12px'
                            }}
                          />
                          
                          {/* Turn direction for turn type */}
                          {marker.type === 'turn' && (
                            <div style={{marginTop: '6px', display: 'flex', gap: '8px'}}>
                              <button
                                onClick={() => {
                                  const updatedMarkers = road.markers.map((m, i) => 
                                    i === mIdx ? {...m, turnDirection: 'left'} : m
                                  );
                                  const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                                  setRoads(updated);
                                }}
                                style={{
                                  flex: 1,
                                  padding: '6px',
                                  backgroundColor: marker.turnDirection === 'left' ? '#FF9800' : '#f5f5f5',
                                  color: marker.turnDirection === 'left' ? 'white' : '#333',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                ⬅️ Turn Left
                              </button>
                              <button
                                onClick={() => {
                                  const updatedMarkers = road.markers.map((m, i) => 
                                    i === mIdx ? {...m, turnDirection: 'right'} : m
                                  );
                                  const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                                  setRoads(updated);
                                }}
                                style={{
                                  flex: 1,
                                  padding: '6px',
                                  backgroundColor: marker.turnDirection === 'right' ? '#FF9800' : '#f5f5f5',
                                  color: marker.turnDirection === 'right' ? 'white' : '#333',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                ➡️ Turn Right
                              </button>
                            </div>
                          )}
                          
                          {/* Character selector for character type */}
                          {marker.type === 'character' && (
                            <select
                              value={marker.characterId || ''}
                              onChange={(e) => {
                                const updatedMarkers = road.markers.map((m, i) => 
                                  i === mIdx ? {...m, characterId: e.target.value} : m
                                );
                                const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                                setRoads(updated);
                              }}
                              style={{
                                width: '100%',
                                marginTop: '6px',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '12px'
                              }}
                            >
                              <option value="">-- Select Character --</option>
                              {characters.map(c => (
                                <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Add Marker Button */}
                  <button
                    onClick={() => {
                      const newMarker = {
                        id: `m_${Date.now()}`,
                        x: taxiSettings.position.x,
                        y: taxiSettings.position.y,
                        rotation: taxiSettings.rotation || 0,
                        type: 'waypoint',
                        message: ''
                      };
                      const updatedMarkers = [...(road.markers || []), newMarker];
                      const updated = roads.map(r => r.id === road.id ? {...r, markers: updatedMarkers} : r);
                      setRoads(updated);
                    }}
                    style={{
                      marginTop: '10px',
                      padding: '10px 16px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      width: '100%'
                    }}
                  >
                    📍 Add Marker at Taxi Position ({taxiSettings.position.x}, {taxiSettings.position.y})
                  </button>
                  
                  <p style={{color: '#666', fontSize: '11px', marginTop: '8px', textAlign: 'center'}}>
                    💡 Tip: Move the taxi using the controls above, then click "Add Marker" to create waypoints
                  </p>
                </div>
                
                {/* Characters on Road */}
                <div style={{marginTop: '15px'}}>
                  <strong>👤 Characters on this Road:</strong>
                  {road.characters?.map((char, cIdx) => (
                    <div key={cIdx} style={{
                      backgroundColor: '#FFF3E0',
                      padding: '10px',
                      borderRadius: '4px',
                      marginTop: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span>{characters.find(c => c.id === char.character_id)?.emoji || '👤'}</span>
                      <span>{characters.find(c => c.id === char.character_id)?.name || char.character_id}</span>
                      <span style={{color: '#666'}}>at marker #{char.marker_index + 1}</span>
                      <span style={{color: '#4CAF50'}}>+{char.gems_reward} gems</span>
                      <button
                        onClick={() => {
                          const updatedChars = road.characters.filter((_, i) => i !== cIdx);
                          const updated = roads.map(r => r.id === road.id ? {...r, characters: updatedChars} : r);
                          setRoads(updated);
                        }}
                        style={{
                          marginLeft: 'auto',
                          padding: '4px 8px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  
                  {/* Add Character to Road */}
                  <div style={{marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <select
                      id={`char_select_${road.id}`}
                      style={{...styles.select, width: '150px'}}
                    >
                      <option value="">Select Character</option>
                      {characters.map(c => (
                        <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>
                      ))}
                    </select>
                    <select
                      id={`marker_select_${road.id}`}
                      style={{...styles.select, width: '120px'}}
                    >
                      <option value="">At Marker</option>
                      {road.markers?.map((_, i) => (
                        <option key={i} value={i}>Marker #{i + 1}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      id={`gems_${road.id}`}
                      placeholder="Gems"
                      defaultValue={5}
                      style={{...styles.input, width: '70px'}}
                    />
                    <button
                      onClick={() => {
                        const charId = document.getElementById(`char_select_${road.id}`).value;
                        const markerIdx = parseInt(document.getElementById(`marker_select_${road.id}`).value);
                        const gems = parseInt(document.getElementById(`gems_${road.id}`).value) || 5;
                        
                        if (!charId || isNaN(markerIdx)) return;
                        
                        const newChar = {
                          character_id: charId,
                          marker_index: markerIdx,
                          gems_reward: gems
                        };
                        const updatedChars = [...(road.characters || []), newChar];
                        const updated = roads.map(r => r.id === road.id ? {...r, characters: updatedChars} : r);
                        setRoads(updated);
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        })}
        
        {/* Add New Road */}
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '8px',
          marginTop: '15px'
        }}>
          <strong>➕ Create New Road:</strong>
          <div style={{display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center'}}>
            <select
              id="new_road_from"
              style={{...styles.select, width: '150px'}}
            >
              <option value="">From Location</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <span style={{fontSize: '20px'}}>→</span>
            <select
              id="new_road_to"
              style={{...styles.select, width: '150px'}}
            >
              <option value="">To Location</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <input
              type="text"
              id="new_road_name"
              placeholder="Road Name"
              style={{...styles.input, width: '150px'}}
            />
            <button
              onClick={() => {
                const from = document.getElementById('new_road_from').value;
                const to = document.getElementById('new_road_to').value;
                const name = document.getElementById('new_road_name').value || 'New Road';
                
                if (!from || !to || from === to) {
                  alert('Please select different From and To locations');
                  return;
                }
                
                const newRoad = {
                  id: `road_${Date.now()}`,
                  name: name,
                  connects: [from, to],
                  markers: [],
                  characters: [],
                  visible: true,
                  bidirectional: true
                };
                setRoads([...roads, newRoad]);
                setSelectedRoadId(newRoad.id);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              + Create Road
            </button>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div style={styles.instructionsBox}>
        <h4>📖 How to Use:</h4>
        <ul>
          <li>Adjust taxi position using X/Y/Z sliders above</li>
          <li>Set rotation for the direction taxi should face</li>
          <li>Click "Add Marker" to save current position as a stop</li>
          <li>Taxi will travel through markers in order during gameplay</li>
          <li>Each marker will use the rotation you set at that point</li>
          <li>Animation speed controls how fast taxi moves between stops</li>
          <li><strong>🛣️ Road Network:</strong> Create roads to connect locations. Taxi finds paths automatically!</li>
        </ul>
      </div>
    </div>
  );
}

// Gems Tab Component (continued in next file due to length)
function GemsTab({ hiddenGems, setHiddenGems, selectedGemId, setSelectedGemId, dayNightSettings, setDayNightSettings, gemFeatures, setGemFeatures, gameSettings, updateSettings, saveGameData }) {
  const addGem = (type) => {
    const values = { ruby: 100, emerald: 150, diamond: 300 };
    const newGem = {
      id: `gem_${Date.now()}`,
      type,
      value: values[type],
      x: Math.floor(Math.random() * 800) + 200,
      y: Math.floor(Math.random() * 500) + 150,
      z: 0,
      size: 40,
      respawn: true,
      enabled: true
    };
    setHiddenGems([...hiddenGems, newGem]);
    setSelectedGemId(newGem.id);
  };

  const updateGem = (id, field, value) => {
    setHiddenGems(hiddenGems.map(g => g.id === id ? { ...g, [field]: value } : g));
  };

  const deleteGem = (id) => {
    setHiddenGems(hiddenGems.filter(g => g.id !== id));
    if (selectedGemId === id) setSelectedGemId(null);
  };

  const selectedGem = hiddenGems.find(g => g.id === selectedGemId);

  return (
    <div style={styles.tabContent}>
      <h2>💎 Gems & Currency Management</h2>

      {/* Currency Settings */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>💰 Currency Settings</h3>
        
        <label style={styles.label}>
          💎 Starting Gems:
          <input
            type="number"
            value={gameSettings.starting_gems}
            onChange={(e) => updateSettings('starting_gems', parseInt(e.target.value))}
            style={styles.inputSmall}
          />
        </label>

        <label style={styles.label}>
          <input
            type="checkbox"
            checked={gameSettings.requires_membership}
            onChange={(e) => updateSettings('requires_membership', e.target.checked)}
          />
          Require Paid Membership (Check if user is paid member)
        </label>

        {gameSettings.requires_membership && (
          <label style={styles.label}>
            💵 Game Price:
            <input
              type="number"
              value={gameSettings.game_price}
              onChange={(e) => updateSettings('game_price', parseFloat(e.target.value))}
              style={styles.inputSmall}
              step="0.01"
            />
          </label>
        )}

        <button onClick={saveGameData} style={styles.saveButton}>
          💾 Save Currency Settings
        </button>
      </div>

      {/* Day/Night Cycle */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🌞🌙 Day/Night Cycle</h3>
        
        <label style={styles.label}>
          ☀️ Day Duration (minutes):
          <input
            type="number"
            value={dayNightSettings.dayDuration}
            onChange={(e) => setDayNightSettings({...dayNightSettings, dayDuration: parseInt(e.target.value)})}
            style={styles.inputSmall}
            min="1"
            max="60"
          />
        </label>

        <label style={styles.label}>
          🌙 Night Duration (minutes):
          <input
            type="number"
            value={dayNightSettings.nightDuration}
            onChange={(e) => setDayNightSettings({...dayNightSettings, nightDuration: parseInt(e.target.value)})}
            style={styles.inputSmall}
            min="1"
            max="30"
          />
        </label>

        <p style={styles.info}>
          ℹ️ Gems respawn at the start of each new day cycle
        </p>
      </div>

      {/* User Gem Features */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🎁 User Features</h3>
        
        <div style={styles.featureGrid}>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={gemFeatures.dailyLoginRewards}
              onChange={(e) => setGemFeatures({...gemFeatures, dailyLoginRewards: e.target.checked})}
            />
            Daily Login Rewards
          </label>

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={gemFeatures.rankedMode}
              onChange={(e) => setGemFeatures({...gemFeatures, rankedMode: e.target.checked})}
            />
            Ranked Mode Rewards (based on gem count)
          </label>

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={gemFeatures.lowBalanceNotifications}
              onChange={(e) => setGemFeatures({...gemFeatures, lowBalanceNotifications: e.target.checked})}
            />
            Low Balance Notifications
          </label>

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={gemFeatures.spendingLock}
              onChange={(e) => setGemFeatures({...gemFeatures, spendingLock: e.target.checked})}
            />
            Spending Lock (prevent accidental use)
          </label>

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={gemFeatures.smartSuggestions}
              onChange={(e) => setGemFeatures({...gemFeatures, smartSuggestions: e.target.checked})}
            />
            Smart Suggestions ("20 gems away from X")
          </label>

          <label style={styles.label}>
            <input
              type="checkbox"
              checked={gemFeatures.savingsGoals}
              onChange={(e) => setGemFeatures({...gemFeatures, savingsGoals: e.target.checked})}
            />
            Savings Goals ("Collect 500 for Character X")
          </label>
        </div>

        <p style={styles.info}>
          💡 Transaction history is automatically tracked for all users
        </p>
      </div>

      {/* Hidden Gems */}
      <div style={styles.section}>
        <div style={styles.tabHeader}>
          <h3 style={styles.sectionTitle}>💎 Hidden Gems on Map</h3>
          <div style={{display: 'flex', gap: '10px'}}>
            <button onClick={() => addGem('ruby')} style={{...styles.addBtn, background: '#F44336'}}>
              + Ruby (❤️ 100)
            </button>
            <button onClick={() => addGem('emerald')} style={{...styles.addBtn, background: '#4CAF50'}}>
              + Emerald (💚 150)
            </button>
            <button onClick={() => addGem('diamond')} style={{...styles.addBtn, background: '#2196F3'}}>
              + Diamond (💎 300)
            </button>
          </div>
        </div>

        <div style={styles.splitLayout}>
          {/* Gem List */}
          <div style={styles.listPanel}>
            <h4 style={styles.panelTitle}>Placed Gems ({hiddenGems.filter(g => g.enabled).length} active)</h4>
            {hiddenGems.map((gem) => (
              <div
                key={gem.id}
                onClick={() => setSelectedGemId(gem.id)}
                style={{
                  ...styles.listItem,
                  ...(selectedGemId === gem.id ? styles.selectedItem : {})
                }}
              >
                <span style={{fontSize: '20px', marginRight: '8px'}}>
                  {gem.type === 'ruby' ? '❤️' : gem.type === 'emerald' ? '💚' : '💎'}
                </span>
                <span style={{fontWeight: 'bold', textTransform: 'capitalize'}}>{gem.type}</span>
                <span style={{color: '#666', fontSize: '12px'}}>({gem.value} gems)</span>
                {!gem.enabled && <span style={styles.disabledBadge}>Disabled</span>}
                {selectedGemId === gem.id && <span style={styles.highlightBadge}>Selected</span>}
              </div>
            ))}
            {hiddenGems.length === 0 && (
              <div style={styles.emptyState}>No gems placed yet</div>
            )}
          </div>

          {/* Gem Editor */}
          <div style={styles.editorPanel}>
            {selectedGem ? (
              <>
                <h4 style={styles.panelTitle}>
                  Edit {selectedGem.type.charAt(0).toUpperCase() + selectedGem.type.slice(1)} Gem
                </h4>
                
                <label style={styles.label}>
                  X Position:
                  <input
                    type="range"
                    min="0"
                    max="1200"
                    value={selectedGem.x}
                    onChange={(e) => updateGem(selectedGem.id, 'x', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedGem.x}px</span>
                </label>

                <label style={styles.label}>
                  Y Position:
                  <input
                    type="range"
                    min="0"
                    max="800"
                    value={selectedGem.y}
                    onChange={(e) => updateGem(selectedGem.id, 'y', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedGem.y}px</span>
                </label>

                <label style={styles.label}>
                  Z-Axis (depth):
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={selectedGem.z || 0}
                    onChange={(e) => updateGem(selectedGem.id, 'z', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedGem.z || 0}px</span>
                </label>

                <label style={styles.label}>
                  Size:
                  <input
                    type="range"
                    min="20"
                    max="80"
                    value={selectedGem.size || 40}
                    onChange={(e) => updateGem(selectedGem.id, 'size', parseInt(e.target.value))}
                    style={styles.slider}
                  />
                  <span>{selectedGem.size || 40}px</span>
                </label>

                <label style={styles.label}>
                  Gem Value:
                  <input
                    type="number"
                    value={selectedGem.value}
                    onChange={(e) => updateGem(selectedGem.id, 'value', parseInt(e.target.value))}
                    style={styles.inputSmall}
                  />
                </label>

                <div style={styles.checkboxGroup}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={selectedGem.respawn}
                      onChange={(e) => updateGem(selectedGem.id, 'respawn', e.target.checked)}
                    />
                    Respawn Daily
                  </label>

                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={selectedGem.enabled}
                      onChange={(e) => updateGem(selectedGem.id, 'enabled', e.target.checked)}
                    />
                    <strong>Enabled (visible in game)</strong>
                  </label>
                </div>

                <div style={styles.actionButtons}>
                  <button onClick={() => deleteGem(selectedGem.id)} style={styles.deleteButton}>
                    🗑️ Delete Gem
                  </button>
                  <button onClick={saveGameData} style={styles.saveButton}>
                    💾 Save Changes
                  </button>
                </div>
              </>
            ) : (
              <div style={styles.emptyEditor}>
                <p>👈 Select a gem from the list to edit</p>
                <p>or click a gem type button above to add one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Stop Bubble Tab Component
function StopBubbleTab({ stopBubbleSettings, setStopBubbleSettings, saveGameData }) {
  return (
    <div style={styles.tabContent}>
      <h2>🚦 Stop Bubble Settings</h2>
      <p style={{color: '#666', marginBottom: '20px'}}>
        Configure the position and appearance of the bubble that appears when taxi stops at markers.
      </p>

      <div style={{display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px'}}>
        
        {/* X-Axis Position */}
        <div style={styles.control}>
          <label style={styles.label}>
            X-Axis (Left ← → Right): {stopBubbleSettings.offsetX}px
          </label>
          <input
            type="range"
            min="-200"
            max="200"
            value={stopBubbleSettings.offsetX}
            onChange={(e) => setStopBubbleSettings({
              ...stopBubbleSettings,
              offsetX: parseInt(e.target.value)
            })}
            style={{width: '100%'}}
          />
          <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666'}}>
            <span>-200 (Far Left)</span>
            <span>0 (Center)</span>
            <span>+200 (Far Right)</span>
          </div>
        </div>

        {/* Y-Axis Position */}
        <div style={styles.control}>
          <label style={styles.label}>
            Y-Axis (Top ↑ ↓ Down): {stopBubbleSettings.offsetY}px
          </label>
          <input
            type="range"
            min="-200"
            max="200"
            value={stopBubbleSettings.offsetY}
            onChange={(e) => setStopBubbleSettings({
              ...stopBubbleSettings,
              offsetY: parseInt(e.target.value)
            })}
            style={{width: '100%'}}
          />
          <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666'}}>
            <span>-200 (Far Up)</span>
            <span>0 (Center)</span>
            <span>+200 (Far Down)</span>
          </div>
        </div>

        {/* Rotation */}
        <div style={styles.control}>
          <label style={styles.label}>
            Manual Rotation: {stopBubbleSettings.rotation}°
          </label>
          <input
            type="range"
            min="0"
            max="360"
            value={stopBubbleSettings.rotation}
            onChange={(e) => setStopBubbleSettings({
              ...stopBubbleSettings,
              rotation: parseInt(e.target.value)
            })}
            style={{width: '100%'}}
          />
          <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666'}}>
            <span>0° (Normal)</span>
            <span>180° (Upside Down)</span>
            <span>360° (Full Rotation)</span>
          </div>
        </div>

        {/* Size */}
        <div style={styles.control}>
          <label style={styles.label}>
            Size: {stopBubbleSettings.size}px
          </label>
          <input
            type="range"
            min="100"
            max="400"
            value={stopBubbleSettings.size}
            onChange={(e) => setStopBubbleSettings({
              ...stopBubbleSettings,
              size: parseInt(e.target.value)
            })}
            style={{width: '100%'}}
          />
          <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666'}}>
            <span>100px (Small)</span>
            <span>250px (Medium)</span>
            <span>400px (Large)</span>
          </div>
        </div>

        {/* Current Values Summary */}
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '8px',
          border: '2px solid #2196F3'
        }}>
          <h3 style={{marginTop: 0, color: '#2196F3'}}>📊 Current Settings</h3>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px'}}>
            <div><strong>X Offset:</strong> {stopBubbleSettings.offsetX}px</div>
            <div><strong>Y Offset:</strong> {stopBubbleSettings.offsetY}px</div>
            <div><strong>Rotation:</strong> {stopBubbleSettings.rotation}°</div>
            <div><strong>Size:</strong> {stopBubbleSettings.size}px</div>
          </div>
          <p style={{fontSize: '12px', color: '#666', marginBottom: 0, marginTop: '10px'}}>
            💡 Distance from taxi: {Math.round(Math.sqrt(stopBubbleSettings.offsetX ** 2 + stopBubbleSettings.offsetY ** 2))}px
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={saveGameData}
          style={{
            padding: '15px 30px',
            fontSize: '16px',
            fontWeight: 'bold',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          💾 Save Stop Bubble Settings
        </button>
      </div>
    </div>
  );
}

// User Avatar Tab Component
// Player Avatar Tab - Combined Male/Female with subtabs
function PlayerAvatarTab({ userMaleAvatarSettings, setUserMaleAvatarSettings, userFemaleAvatarSettings, setUserFemaleAvatarSettings, saveGameData }) {
  const [avatarGender, setAvatarGender] = useState('male');
  
  return (
    <div style={styles.tabContent}>
      <h2>👤 Player Avatar Settings ("You")</h2>
      
      {/* Gender Subtabs */}
      <div style={{display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '10px'}}>
        <button
          onClick={() => setAvatarGender('male')}
          style={{
            padding: '10px 20px',
            backgroundColor: avatarGender === 'male' ? '#2196F3' : '#f5f5f5',
            color: avatarGender === 'male' ? 'white' : '#333',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontWeight: avatarGender === 'male' ? 'bold' : 'normal',
            fontSize: '14px'
          }}
        >
          👨 Male
        </button>
        <button
          onClick={() => setAvatarGender('female')}
          style={{
            padding: '10px 20px',
            backgroundColor: avatarGender === 'female' ? '#E91E63' : '#f5f5f5',
            color: avatarGender === 'female' ? 'white' : '#333',
            border: 'none',
            borderRadius: '6px 6px 0 0',
            cursor: 'pointer',
            fontWeight: avatarGender === 'female' ? 'bold' : 'normal',
            fontSize: '14px'
          }}
        >
          👩 Female
        </button>
      </div>
      
      {/* Male Avatar Settings */}
      {avatarGender === 'male' && (
        <MaleAvatarSection 
          userMaleAvatarSettings={userMaleAvatarSettings}
          setUserMaleAvatarSettings={setUserMaleAvatarSettings}
          saveGameData={saveGameData}
        />
      )}
      
      {/* Female Avatar Settings */}
      {avatarGender === 'female' && (
        <FemaleAvatarSection
          userFemaleAvatarSettings={userFemaleAvatarSettings}
          setUserFemaleAvatarSettings={setUserFemaleAvatarSettings}
          saveGameData={saveGameData}
        />
      )}
    </div>
  );
}

// Male Avatar Section (inside Player Avatar Tab)
function MaleAvatarSection({ userMaleAvatarSettings, setUserMaleAvatarSettings, saveGameData }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Male Avatar Configuration</h3>
        
        <label style={styles.label}>
          Default Emoji:
          <input
            type="text"
            value={userMaleAvatarSettings.emoji}
            onChange={(e) => setUserMaleAvatarSettings({...userMaleAvatarSettings, emoji: e.target.value})}
            style={styles.inputSmall}
            maxLength="2"
          />
        </label>

        <div style={{marginBottom: '15px'}}>
          <label style={styles.label}>Custom Male Avatar Image:</label>
          <div style={{display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '5px'}}>
            <div style={{flex: 1}}>
              <input
                type="text"
                value={userMaleAvatarSettings.image_url || ''}
                onChange={(e) => setUserMaleAvatarSettings({...userMaleAvatarSettings, image_url: e.target.value})}
                style={styles.input}
                placeholder="Option 2: Or paste Dropbox/URL"
              />
            </div>
            
            <label 
              htmlFor="male-avatar-upload-mjrny"
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              📤 Upload
            </label>
            <input
              id="male-avatar-upload-mjrny"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 5 * 1024 * 1024) {
                    alert('⚠️ Image too large! Max 5MB');
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setUserMaleAvatarSettings({...userMaleAvatarSettings, image_url: event.target.result});
                  };
                  reader.readAsDataURL(file);
                }
              }}
              style={{display: 'none'}}
            />
          </div>
          <p style={{fontSize: '11px', color: '#666', margin: '5px 0 0 0'}}>
            💡 Option 1: Upload from computer (Max 5MB) | Option 2: Paste URL
          </p>
          
          {userMaleAvatarSettings.image_url && (
            <div style={styles.imagePreview}>
              <p style={{fontSize: '12px', color: '#666', margin: '5px 0'}}>Preview:</p>
              <img 
                src={userMaleAvatarSettings.image_url} 
                alt="Male avatar preview"
                style={{maxWidth: '100px', maxHeight: '100px', borderRadius: '4px'}}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <p style={{display: 'none', color: '#f44336', fontSize: '12px'}}>❌ Failed to load image</p>
            </div>
          )}
        </div>

        <div style={styles.controlGrid}>
          <label style={styles.label}>
            X-Axis (Starting Position):
            <input
              type="range"
              min="0"
              max="1200"
              value={userMaleAvatarSettings.position.x}
              onChange={(e) => setUserMaleAvatarSettings({...userMaleAvatarSettings, position: {...userMaleAvatarSettings.position, x: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{userMaleAvatarSettings.position.x}px</span>
          </label>

          <label style={styles.label}>
            Y-Axis (Starting Position):
            <input
              type="range"
              min="0"
              max="800"
              value={userMaleAvatarSettings.position.y}
              onChange={(e) => setUserMaleAvatarSettings({...userMaleAvatarSettings, position: {...userMaleAvatarSettings.position, y: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{userMaleAvatarSettings.position.y}px</span>
          </label>

          <label style={styles.label}>
            Size:
            <input
              type="range"
              min="40"
              max="150"
              value={userMaleAvatarSettings.size}
              onChange={(e) => setUserMaleAvatarSettings({...userMaleAvatarSettings, size: parseInt(e.target.value)})}
              style={styles.slider}
            />
            <span>{userMaleAvatarSettings.size}px</span>
          </label>
          
          <label style={styles.label}>
            Rotation:
            <input
              type="range"
              min="0"
              max="360"
              value={userMaleAvatarSettings.rotation || 0}
              onChange={(e) => setUserMaleAvatarSettings({...userMaleAvatarSettings, rotation: parseInt(e.target.value)})}
              style={styles.slider}
            />
            <span>{userMaleAvatarSettings.rotation || 0}°</span>
          </label>
        </div>

        <button onClick={saveGameData} style={styles.saveButton}>
          💾 Save Male Avatar Settings
        </button>
      </div>
  );
}

// Female Avatar Section (inside Player Avatar Tab)
function FemaleAvatarSection({ userFemaleAvatarSettings, setUserFemaleAvatarSettings, saveGameData }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Female Avatar Configuration</h3>
        
        <label style={styles.label}>
          Default Emoji:
          <input
            type="text"
            value={userFemaleAvatarSettings.emoji}
            onChange={(e) => setUserFemaleAvatarSettings({...userFemaleAvatarSettings, emoji: e.target.value})}
            style={styles.inputSmall}
            maxLength="2"
          />
        </label>

        <div style={{marginBottom: '15px'}}>
          <label style={styles.label}>Custom Female Avatar Image:</label>
          <div style={{display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '5px'}}>
            <div style={{flex: 1}}>
              <input
                type="text"
                value={userFemaleAvatarSettings.image_url || ''}
                onChange={(e) => setUserFemaleAvatarSettings({...userFemaleAvatarSettings, image_url: e.target.value})}
                style={styles.input}
                placeholder="Option 2: Or paste Dropbox/URL"
              />
            </div>
            
            <label 
              htmlFor="female-avatar-upload-mjrny"
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              📤 Upload
            </label>
            <input
              id="female-avatar-upload-mjrny"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 5 * 1024 * 1024) {
                    alert('⚠️ Image too large! Max 5MB');
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setUserFemaleAvatarSettings({...userFemaleAvatarSettings, image_url: event.target.result});
                  };
                  reader.readAsDataURL(file);
                }
              }}
              style={{display: 'none'}}
            />
          </div>
          <p style={{fontSize: '11px', color: '#666', margin: '5px 0 0 0'}}>
            💡 Option 1: Upload from computer (Max 5MB) | Option 2: Paste URL
          </p>
          
          {userFemaleAvatarSettings.image_url && (
            <div style={styles.imagePreview}>
              <p style={{fontSize: '12px', color: '#666', margin: '5px 0'}}>Preview:</p>
              <img 
                src={userFemaleAvatarSettings.image_url} 
                alt="Female avatar preview"
                style={{maxWidth: '100px', maxHeight: '100px', borderRadius: '4px'}}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <p style={{display: 'none', color: '#f44336', fontSize: '12px'}}>❌ Failed to load image</p>
            </div>
          )}
        </div>

        <div style={styles.controlGrid}>
          <label style={styles.label}>
            X-Axis (Starting Position):
            <input
              type="range"
              min="0"
              max="1200"
              value={userFemaleAvatarSettings.position.x}
              onChange={(e) => setUserFemaleAvatarSettings({...userFemaleAvatarSettings, position: {...userFemaleAvatarSettings.position, x: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{userFemaleAvatarSettings.position.x}px</span>
          </label>

          <label style={styles.label}>
            Y-Axis (Starting Position):
            <input
              type="range"
              min="0"
              max="800"
              value={userFemaleAvatarSettings.position.y}
              onChange={(e) => setUserFemaleAvatarSettings({...userFemaleAvatarSettings, position: {...userFemaleAvatarSettings.position, y: parseInt(e.target.value)}})}
              style={styles.slider}
            />
            <span>{userFemaleAvatarSettings.position.y}px</span>
          </label>

          <label style={styles.label}>
            Size:
            <input
              type="range"
              min="40"
              max="150"
              value={userFemaleAvatarSettings.size}
              onChange={(e) => setUserFemaleAvatarSettings({...userFemaleAvatarSettings, size: parseInt(e.target.value)})}
              style={styles.slider}
            />
            <span>{userFemaleAvatarSettings.size}px</span>
          </label>
          
          <label style={styles.label}>
            Rotation:
            <input
              type="range"
              min="0"
              max="360"
              value={userFemaleAvatarSettings.rotation || 0}
              onChange={(e) => setUserFemaleAvatarSettings({...userFemaleAvatarSettings, rotation: parseInt(e.target.value)})}
              style={styles.slider}
            />
            <span>{userFemaleAvatarSettings.rotation || 0}°</span>
          </label>
        </div>

        <button onClick={saveGameData} style={styles.saveButton}>
          💾 Save Female Avatar Settings
        </button>
      </div>
  );
}

// Player Management Tab Component
function PlayerManagementTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchPlayer = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      // TODO: Implement actual player search API
      const response = await axios.get(`${API_URL}/api/game/players/search?q=${searchQuery}`);
      setPlayers(response.data.players || []);
    } catch (error) {
      console.error('Search error:', error);
      alert('Player search not implemented yet');
    }
    setLoading(false);
  };

  const updatePlayerBalance = async (playerId, field, value) => {
    try {
      await axios.post(`${API_URL}/api/game/players/${playerId}/balance`, {
        [field]: value
      });
      alert('✅ Balance updated!');
    } catch (error) {
      console.error('Update error:', error);
      alert('❌ Failed to update balance');
    }
  };

  return (
    <div style={styles.tabContent}>
      <h2>👥 Player Management</h2>
      
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Search Players</h3>
        
        <div style={{display: 'flex', gap: '10px', marginBottom: '20px'}}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by username or ID..."
            style={{...styles.input, flex: 1}}
            onKeyPress={(e) => e.key === 'Enter' && searchPlayer()}
          />
          <button onClick={searchPlayer} style={styles.addBtn}>
            🔍 Search
          </button>
        </div>

        {loading && <p>Searching...</p>}

        {players.length > 0 && (
          <div style={styles.locationList}>
            {players.map(player => (
              <div
                key={player.id}
                onClick={() => setSelectedPlayer(player)}
                style={{
                  ...styles.listItem,
                  ...(selectedPlayer?.id === player.id ? styles.selectedItem : {})
                }}
              >
                <span>{player.username || player.id}</span>
                <span style={{color: '#5C6BC0'}}>💎 {player.gems_balance}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPlayer && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Player: {selectedPlayer.username || selectedPlayer.id}</h3>
          
          <label style={styles.label}>
            Gems Balance:
            <input
              type="number"
              value={selectedPlayer.gems_balance}
              onChange={(e) => setSelectedPlayer({...selectedPlayer, gems_balance: parseInt(e.target.value)})}
              style={styles.inputSmall}
            />
            <button 
              onClick={() => updatePlayerBalance(selectedPlayer.id, 'gems_balance', selectedPlayer.gems_balance)}
              style={{...styles.addBtn, marginLeft: '10px'}}
            >
              Update
            </button>
          </label>

          <h4>📜 Transaction History</h4>
          <div style={styles.transactionList}>
            {(selectedPlayer.transactions || []).map((tx, idx) => (
              <div key={idx} style={styles.transactionItem}>
                <span>{tx.item}</span>
                <span style={{color: tx.amount > 0 ? '#4CAF50' : '#f44336'}}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount} 💎
                </span>
                <span style={{fontSize: '11px', color: '#999'}}>
                  {new Date(tx.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
            {(!selectedPlayer.transactions || selectedPlayer.transactions.length === 0) && (
              <p style={styles.emptyMessage}>No transactions yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Conversations Tab Component
function ConversationsTab({ locations, characters }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiStatus, setAiStatus] = useState({ available: false, provider: 'none' });

  useEffect(() => {
    loadConversations();
    loadAIStatus();
  }, []);

  const loadConversations = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📥 Loading conversations from:', `${API_URL}/api/morocco-conversations/conversations`);
      const response = await axios.get(`${API_URL}/api/morocco-conversations/conversations`);
      console.log('✅ Conversations loaded:', response.data);
      setConversations(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('❌ Failed to load conversations:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to load conversations');
      setLoading(false);
    }
  };

  const loadAIStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/morocco-conversations/ai-status`);
      setAiStatus(response.data);
    } catch (error) {
      console.error('Failed to load AI status:', error);
      setAiStatus({ available: false, provider: 'none', error: error.message });
    }
  };

  const createNewConversation = () => {
    const newConv = {
      id: `conv_${Date.now()}`,
      character_id: `char_${Date.now()}`,
      character_name: 'New Character',
      title: 'New Conversation',
      start_node_id: 'node_start',
      repeatable: true,
      ai_enabled: false,
      ai_context: 'Character personality and teaching focus...',
      nodes: [
        {
          id: 'node_start',
          character_message: 'Hello! This is the start of the conversation.',
          character_emotion: 'neutral',
          choices: [
            {
              id: 'choice_1',
              text: 'Continue',
              next_node_id: null
            }
          ],
          triggers_quest: null,
          gives_gems: 0
        }
      ],
      requires_quest: null
    };
    setSelectedConversation(newConv);
  };

  const saveConversation = async (conversation) => {
    try {
      console.log('💾 Saving conversation:', conversation.id);
      const response = await axios.post(`${API_URL}/api/morocco-conversations/conversations`, conversation);
      console.log('✅ Conversation saved:', response.data);
      alert('✅ Conversation saved successfully!');
      loadConversations();
    } catch (error) {
      console.error('❌ Failed to save conversation:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert('❌ Failed to save conversation: ' + errorMsg);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      console.log('🗑️ Deleting conversation:', conversationId);
      const response = await axios.delete(`${API_URL}/api/morocco-conversations/conversation/${conversationId}`);
      console.log('✅ Conversation deleted:', response.data);
      alert('✅ Conversation deleted!');
      setSelectedConversation(null);
      loadConversations();
    } catch (error) {
      console.error('❌ Failed to delete conversation:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert('❌ Failed to delete conversation: ' + errorMsg);
    }
  };

  const addNode = (conversation) => {
    const newNodeId = `node_${Date.now()}`;
    const newNode = {
      id: newNodeId,
      character_message: 'New message...',
      character_emotion: 'neutral',
      choices: [
        {
          id: `choice_${Date.now()}`,
          text: 'Option 1',
          next_node_id: null
        }
      ],
      triggers_quest: null,
      gives_gems: 0
    };
    
    const updatedConv = {
      ...conversation,
      nodes: [...conversation.nodes, newNode]
    };
    setSelectedConversation(updatedConv);
  };

  const updateNode = (conversation, nodeId, updates) => {
    const updatedNodes = conversation.nodes.map(node =>
      node.id === nodeId ? { ...node, ...updates } : node
    );
    setSelectedConversation({ ...conversation, nodes: updatedNodes });
  };

  const deleteNode = (conversation, nodeId) => {
    if (!window.confirm('Delete this node?')) return;
    const updatedNodes = conversation.nodes.filter(node => node.id !== nodeId);
    setSelectedConversation({ ...conversation, nodes: updatedNodes });
  };

  const addChoice = (conversation, nodeId) => {
    const updatedNodes = conversation.nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          choices: [
            ...node.choices,
            {
              id: `choice_${Date.now()}`,
              text: 'New choice',
              next_node_id: null
            }
          ]
        };
      }
      return node;
    });
    setSelectedConversation({ ...conversation, nodes: updatedNodes });
  };

  const updateChoice = (conversation, nodeId, choiceId, updates) => {
    const updatedNodes = conversation.nodes.map(node => {
      if (node.id === nodeId) {
        const updatedChoices = node.choices.map(choice =>
          choice.id === choiceId ? { ...choice, ...updates } : choice
        );
        return { ...node, choices: updatedChoices };
      }
      return node;
    });
    setSelectedConversation({ ...conversation, nodes: updatedNodes });
  };

  const deleteChoice = (conversation, nodeId, choiceId) => {
    const updatedNodes = conversation.nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          choices: node.choices.filter(choice => choice.id !== choiceId)
        };
      }
      return node;
    });
    setSelectedConversation({ ...conversation, nodes: updatedNodes });
  };

  if (loading) {
    return (
      <div style={styles.tabContent}>
        <h2>💬 Conversation Management</h2>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          border: '2px dashed #ddd'
        }}>
          <div style={{fontSize: '48px', marginBottom: '15px'}}>⏳</div>
          <p style={{fontSize: '16px', color: '#666', marginBottom: '10px'}}>Loading conversations...</p>
          <p style={{fontSize: '14px', color: '#999'}}>Please wait while we fetch the data from the server</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.tabContent}>
        <h2>💬 Conversation Management</h2>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#ffebee',
          borderRadius: '8px',
          border: '2px solid #f44336'
        }}>
          <div style={{fontSize: '48px', marginBottom: '15px'}}>⚠️</div>
          <p style={{fontSize: '16px', color: '#c62828', marginBottom: '10px'}}>Failed to load conversations</p>
          <p style={{fontSize: '14px', color: '#666', marginBottom: '20px'}}>{error}</p>
          <button
            onClick={loadConversations}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.tabContent}>
      <h2>💬 Conversation Management</h2>
      <p style={styles.subtitle}>Create branching conversations for characters in your game</p>

      {/* AI Status Banner */}
      <div style={{
        padding: '10px 15px',
        marginBottom: '20px',
        background: aiStatus.available ? '#d4edda' : '#fff3cd',
        border: `1px solid ${aiStatus.available ? '#c3e6cb' : '#ffeeba'}`,
        borderRadius: '8px',
        fontSize: '14px'
      }}>
        <strong>🤖 AI Status:</strong> {aiStatus.available ? (
          <span style={{ color: '#155724' }}>✅ Connected to {aiStatus.provider} ({aiStatus.model})</span>
        ) : (
          <span style={{ color: '#856404' }}>
            ⚠️ AI Not Configured - Using predefined conversations only. 
            <a href="/AI_INTEGRATION_GUIDE.md" style={{ marginLeft: '5px', color: '#004085' }}>Setup Guide</a>
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '20px', height: '70vh' }}>
        {/* Conversation List */}
        <div style={{ flex: '0 0 300px', borderRight: '2px solid #ddd', paddingRight: '15px', overflowY: 'auto' }}>
          <button
            onClick={createNewConversation}
            style={{
              ...styles.button,
              width: '100%',
              marginBottom: '15px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            ➕ New Conversation
          </button>

          <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>
            Conversations ({conversations.length})
          </h3>

          {conversations.map(conv => (
            <div
              key={conv.id}
              onClick={() => setSelectedConversation(conv)}
              style={{
                padding: '12px',
                marginBottom: '8px',
                background: selectedConversation?.id === conv.id ? '#e3f2fd' : '#f8f9fa',
                border: `2px solid ${selectedConversation?.id === conv.id ? '#2196f3' : '#dee2e6'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{conv.title}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{conv.character_name}</div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                {conv.nodes?.length || 0} nodes {conv.ai_enabled ? '• 🤖 AI' : ''}
              </div>
            </div>
          ))}

          {conversations.length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px', fontSize: '14px' }}>
              No conversations yet.<br />Click "New Conversation" to create one.
            </div>
          )}
        </div>

        {/* Conversation Editor */}
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '10px' }}>
          {selectedConversation ? (
            <div>
              {/* Conversation Header */}
              <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={styles.label}>Conversation Title</label>
                  <input
                    type="text"
                    value={selectedConversation.title}
                    onChange={(e) => setSelectedConversation({
                      ...selectedConversation,
                      title: e.target.value
                    })}
                    style={styles.input}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={styles.label}>Character Name</label>
                    <input
                      type="text"
                      value={selectedConversation.character_name}
                      onChange={(e) => setSelectedConversation({
                        ...selectedConversation,
                        character_name: e.target.value
                      })}
                      style={styles.input}
                    />
                  </div>

                  <div>
                    <label style={styles.label}>Character ID</label>
                    <input
                      type="text"
                      value={selectedConversation.character_id}
                      onChange={(e) => setSelectedConversation({
                        ...selectedConversation,
                        character_id: e.target.value
                      })}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '15px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedConversation.repeatable}
                      onChange={(e) => setSelectedConversation({
                        ...selectedConversation,
                        repeatable: e.target.checked
                      })}
                    />
                    <span>Repeatable</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedConversation.ai_enabled}
                      onChange={(e) => setSelectedConversation({
                        ...selectedConversation,
                        ai_enabled: e.target.checked
                      })}
                      disabled={!aiStatus.available}
                    />
                    <span>🤖 AI-Powered {!aiStatus.available && '(Configure AI first)'}</span>
                  </label>
                </div>

                {selectedConversation.ai_enabled && (
                  <div style={{ marginTop: '15px' }}>
                    <label style={styles.label}>AI Context (Character Personality)</label>
                    <textarea
                      value={selectedConversation.ai_context}
                      onChange={(e) => setSelectedConversation({
                        ...selectedConversation,
                        ai_context: e.target.value
                      })}
                      style={{ ...styles.input, minHeight: '80px' }}
                      placeholder="Describe the character's personality and teaching focus..."
                    />
                  </div>
                )}

                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => saveConversation(selectedConversation)}
                    style={{ ...styles.button, background: '#28a745', color: 'white' }}
                  >
                    💾 Save Conversation
                  </button>
                  <button
                    onClick={() => deleteConversation(selectedConversation.id)}
                    style={{ ...styles.button, background: '#dc3545', color: 'white' }}
                  >
                    🗑️ Delete
                  </button>
                  <button
                    onClick={() => setSelectedConversation(null)}
                    style={styles.button}
                  >
                    ❌ Close
                  </button>
                </div>
              </div>

              {/* Nodes List */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3>Conversation Nodes ({selectedConversation.nodes?.length || 0})</h3>
                  <button
                    onClick={() => addNode(selectedConversation)}
                    style={{ ...styles.button, background: '#17a2b8', color: 'white' }}
                  >
                    ➕ Add Node
                  </button>
                </div>

                {selectedConversation.nodes?.map((node, index) => (
                  <div
                    key={node.id}
                    style={{
                      background: node.id === selectedConversation.start_node_id ? '#fff3cd' : 'white',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '15px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <strong style={{ fontSize: '16px' }}>
                        {node.id === selectedConversation.start_node_id && '🎬 '}
                        Node: {node.id}
                      </strong>
                      <button
                        onClick={() => deleteNode(selectedConversation, node.id)}
                        style={{ ...styles.button, background: '#dc3545', color: 'white', padding: '5px 10px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={styles.label}>Character Message</label>
                      <textarea
                        value={node.character_message}
                        onChange={(e) => updateNode(selectedConversation, node.id, { character_message: e.target.value })}
                        style={{ ...styles.input, minHeight: '60px' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                      <div>
                        <label style={styles.label}>Emotion</label>
                        <select
                          value={node.character_emotion}
                          onChange={(e) => updateNode(selectedConversation, node.id, { character_emotion: e.target.value })}
                          style={styles.input}
                        >
                          <option value="neutral">😐 Neutral</option>
                          <option value="happy">😊 Happy</option>
                          <option value="excited">🤩 Excited</option>
                          <option value="sad">😢 Sad</option>
                          <option value="angry">😠 Angry</option>
                        </select>
                      </div>

                      <div>
                        <label style={styles.label}>Gives Gems</label>
                        <input
                          type="number"
                          value={node.gives_gems || 0}
                          onChange={(e) => updateNode(selectedConversation, node.id, { gives_gems: parseInt(e.target.value) || 0 })}
                          style={styles.input}
                        />
                      </div>

                      <div>
                        <label style={styles.label}>Triggers Quest</label>
                        <input
                          type="text"
                          value={node.triggers_quest || ''}
                          onChange={(e) => updateNode(selectedConversation, node.id, { triggers_quest: e.target.value || null })}
                          style={styles.input}
                          placeholder="quest_id"
                        />
                      </div>
                    </div>

                    {/* Choices */}
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <strong>Player Choices ({node.choices?.length || 0})</strong>
                        <button
                          onClick={() => addChoice(selectedConversation, node.id)}
                          style={{ ...styles.button, padding: '5px 10px', fontSize: '12px' }}
                        >
                          ➕ Add Choice
                        </button>
                      </div>

                      {node.choices?.map((choice, choiceIndex) => (
                        <div
                          key={choice.id}
                          style={{
                            background: '#f8f9fa',
                            padding: '10px',
                            borderRadius: '6px',
                            marginBottom: '8px'
                          }}
                        >
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: '10px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={choice.text}
                              onChange={(e) => updateChoice(selectedConversation, node.id, choice.id, { text: e.target.value })}
                              style={{ ...styles.input, marginBottom: 0 }}
                              placeholder="Choice text"
                            />
                            
                            <select
                              value={choice.next_node_id || ''}
                              onChange={(e) => updateChoice(selectedConversation, node.id, choice.id, { next_node_id: e.target.value || null })}
                              style={{ ...styles.input, marginBottom: 0 }}
                            >
                              <option value="">End conversation</option>
                              {selectedConversation.nodes.map(n => (
                                <option key={n.id} value={n.id}>→ {n.id}</option>
                              ))}
                            </select>

                            <input
                              type="number"
                              value={choice.requires_gems || 0}
                              onChange={(e) => updateChoice(selectedConversation, node.id, choice.id, { requires_gems: parseInt(e.target.value) || null })}
                              style={{ ...styles.input, marginBottom: 0 }}
                              placeholder="Cost"
                            />

                            <button
                              onClick={() => deleteChoice(selectedConversation, node.id, choice.id)}
                              style={{ ...styles.button, background: '#dc3545', color: 'white', padding: '5px 10px', fontSize: '11px' }}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
              <h3>Select a conversation to edit</h3>
              <p>or create a new one to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Settings Tab Component
function SettingsMJourneyTab({ backgroundUrl, setBackgroundUrl, gameSettings, updateSettings, saveGameData }) {
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const updateBackgroundWithAutoSave = (url) => {
    setBackgroundUrl(url);
    
    // Auto-save after 1 second (longer for background to allow typing)
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
    const timeout = setTimeout(() => {
      saveGameData();
    }, 1000);
    setAutoSaveTimeout(timeout);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ Image too large! Please use an image under 5MB.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('⚠️ Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    setUploadingImage(true);

    try {
      // Convert to base64 data URL
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl = event.target.result;
        
        // Set the background URL to the data URL
        setBackgroundUrl(dataUrl);
        
        // Save immediately
        await saveGameData();
        
        setUploadingImage(false);
        alert('✅ Image uploaded and saved successfully!');
      };
      
      reader.onerror = () => {
        setUploadingImage(false);
        alert('❌ Failed to read image file');
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadingImage(false);
      alert('❌ Upload failed: ' + error.message);
    }
  };

  return (
    <div style={styles.tabContent}>
      <h2>⚙️ Game Settings</h2>

      {/* Background Controls */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🖼️ Background Controls</h3>
        
        <div style={{display: 'flex', gap: '15px', alignItems: 'flex-start', marginBottom: '15px'}}>
          <div style={{flex: 1}}>
            <label style={styles.label}>
              Background Image URL (Dropbox/Direct):
              <input
                type="text"
                value={backgroundUrl}
                onChange={(e) => updateBackgroundWithAutoSave(e.target.value)}
                style={styles.input}
                placeholder="https://... or Dropbox link (auto-saves after 1 sec)"
              />
            </label>
          </div>
          
          <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
            <label 
              htmlFor="bg-image-upload"
              style={{
                padding: '12px 24px',
                backgroundColor: uploadingImage ? '#9E9E9E' : '#2196F3',
                color: 'white',
                borderRadius: '6px',
                cursor: uploadingImage ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                fontSize: '14px'
              }}
            >
              {uploadingImage ? '⏳ Uploading...' : '📤 Upload Image'}
            </label>
            <input
              id="bg-image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              style={{display: 'none'}}
            />
            <p style={{fontSize: '11px', color: '#666', margin: 0}}>
              (Max 5MB)
            </p>
          </div>
        </div>

        <p style={styles.info}>
          💡 Option 1: Paste Dropbox link | Option 2: Click "Upload Image" to upload from your computer
        </p>
        
        {backgroundUrl && (
          <div style={{marginTop: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px'}}>
            <p style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '10px'}}>📷 Background Preview:</p>
            <img 
              src={backgroundUrl}
              alt="Background preview"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: '8px',
                border: '2px solid #ddd',
                display: 'block'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <p style={{display: 'none', color: '#f44336', marginTop: '10px'}}>
              ❌ Failed to load image preview. The image will still be saved - check game canvas to verify it works.
            </p>
          </div>
        )}

        {backgroundUrl && backgroundUrl.startsWith('http') && (
          <div style={styles.preview}>
            <p style={{fontWeight: 'bold', marginBottom: '10px'}}>Preview:</p>
            <img 
              src={backgroundUrl} 
              alt="Background preview" 
              style={{maxWidth: '100%', maxHeight: '300px', borderRadius: '8px'}}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <p style={{display: 'none', color: '#f44336', fontSize: '12px'}}>❌ Failed to load preview (may still work in game)</p>
          </div>
        )}

        <button onClick={saveGameData} style={styles.saveButton}>
          💾 Save Now (or wait 1 sec for auto-save)
        </button>
      </div>

      {/* Access Control */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🔒 Access Control</h3>
        
        <label style={styles.label}>
          <input
            type="checkbox"
            checked={gameSettings.requires_membership}
            onChange={(e) => updateSettings('requires_membership', e.target.checked)}
          />
          Require Paid Membership
        </label>

        <p style={styles.info}>
          ℹ️ Membership verification works across all pages (editor and game) automatically.
          The game opens in the same tab with authentication preserved.
        </p>
      </div>
    </div>
  );
}

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#333'
  },
  saveBtn: {
    padding: '12px 24px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  previewBtn: {
    padding: '12px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  saveMessage: {
    padding: '12px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '6px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  tab: {
    padding: '12px 24px',
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#666'
  },
  activeTab: {
    backgroundColor: '#5C6BC0',
    color: 'white',
    fontWeight: 'bold'
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '30px',
    minHeight: '500px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  tabContent: {
    width: '100%'
  },
  tabHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  addBtn: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  splitLayout: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '20px',
    marginTop: '20px'
  },
  listPanel: {
    backgroundColor: '#f9f9f9',
    padding: '15px',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    maxHeight: '600px',
    overflowY: 'auto'
  },
  editorPanel: {
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '6px',
    border: '1px solid #e0e0e0'
  },
  panelTitle: {
    margin: '0 0 15px 0',
    fontSize: '16px',
    color: '#333'
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  selectedItem: {
    border: '2px solid #5C6BC0',
    backgroundColor: '#E8EAF6',
    fontWeight: 'bold'
  },
  highlightBadge: {
    marginLeft: 'auto',
    padding: '4px 8px',
    backgroundColor: '#5C6BC0',
    color: 'white',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  highlightIndicator: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    backgroundColor: '#E8EAF6',
    borderRadius: '6px',
    border: '2px solid #5C6BC0'
  },
  translatorBadge: {
    marginLeft: '8px',
    padding: '4px 8px',
    backgroundColor: '#FF9800',
    color: 'white',
    borderRadius: '4px',
    fontSize: '11px'
  },
  disabledBadge: {
    marginLeft: '8px',
    padding: '4px 8px',
    backgroundColor: '#999',
    color: 'white',
    borderRadius: '4px',
    fontSize: '11px'
  },
  emptyEditor: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999',
    fontSize: '16px'
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '14px',
    color: '#555',
    marginBottom: '15px'
  },
  input: {
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  inputSmall: {
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    width: '120px'
  },
  textarea: {
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  slider: {
    width: '100%',
    marginRight: '10px'
  },
  controlsSection: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #e0e0e0'
  },
  checkboxGroup: {
    display: 'flex',
    gap: '20px',
    marginTop: '10px',
    flexWrap: 'wrap'
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
    justifyContent: 'flex-end'
  },
  deleteButton: {
    padding: '10px 20px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  section: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    margin: '0 0 15px 0',
    fontSize: '18px',
    color: '#333'
  },
  controlGrid: {
    display: 'grid',
    gap: '15px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
  },
  markerList: {
    display: 'grid',
    gap: '15px'
  },
  markerCard: {
    backgroundColor: 'white',
    padding: '15px',
    borderRadius: '6px',
    border: '1px solid #ddd'
  },
  markerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px'
  },
  positionDisplay: {
    display: 'flex',
    gap: '15px',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    fontSize: '14px',
    flexWrap: 'wrap',
    marginBottom: '10px'
  },
  instructionsBox: {
    padding: '20px',
    backgroundColor: '#E3F2FD',
    borderRadius: '8px',
    marginTop: '20px'
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px'
  },
  info: {
    padding: '10px',
    backgroundColor: '#E8F5E9',
    borderRadius: '4px',
    margin: '10px 0',
    fontSize: '14px',
    color: '#2E7D32'
  },
  preview: {
    marginTop: '15px',
    padding: '15px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #ddd'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#999',
    fontSize: '14px'
  },
  subtitle: {
    color: '#666',
    marginBottom: '20px'
  },
  comingSoon: {
    padding: '60px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#999'
  },
  deleteBtn: {
    padding: '6px 12px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '24px',
    color: '#666'
  },
  imagePreview: {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    border: '1px solid #ddd'
  },
  transactionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '10px',
    maxHeight: '300px',
    overflowY: 'auto'
  },
  transactionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    fontSize: '13px',
    gap: '10px'
  }
};

export default AdminGameEditor;
